import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { acquireSharedConnection, dedupedSharedConnect, releaseSharedConnection } from "./connection/pool"
import type { GameConnection, ServerEvent, Snapshot } from "./types"
import { fmtCompact, toNum } from "./format"
import { isSnowflake } from "@cookies/shared/snowflake"
import { apiFetch } from "../auth/api"
import { QUESTS, ABILITIES, COSMETICS, costFor } from "@cookies/shared/catalog/display"
import { displayNameFor, escalateCostStr, upgradeDefFor, projectPersonalFeeStr, nextUpgradeCostStr } from "./bakeryStore/costs"
import { friendlyJoinError, isJoinRelatedError, bakerCreditsFor, friendlyBuyError, buyFailureText, buySuccessText } from "./bakeryStore/errors"
import { effectivePower, comboMult, previewPerClick, CLICK_COOKIE_MULT } from "./bakeryStore/click"
import { questCurrent } from "./bakeryStore/quests"
import { hasSeqGap } from "@cookies/shared/protocol"
import { BAKERY_LIVE_TICK_MS, API_RESYNC_THROTTLE_MS, API_BACKOFF_MAX_MS, NOTICE_OK_MS, NOTICE_ERR_MS, QUEST_SYNC_THROTTLE_MS, API_TIMEOUT_MS, API_GRACE_MS } from "@cookies/shared/config"
import { sfxAbility, sfxBuy } from "./music"
import { newRequestId } from "./bakeryStore/request"
import { liveBankFor as sharedLiveBankFor } from "./bakeryStore/liveBank"
import type { StoreQuest, SneakyFlash, MiniFlash, GoldenFlash, BuyNotice, PurchaseRecord } from "./bakeryStore/types"

export type { BuyFailureInfo } from "./bakeryStore/errors"
export type { StoreQuest, SneakyFlash, MiniFlash, GoldenFlash, BuyNotice, PurchaseRecord }
// Fast-buy: single WS msg per tap (requestId), pure optimistic apply shown
// instantly, server ack/broadcast/snapshot reconcile authoritative BigInt
// strings. Sends are fire-and-forget (GameConnection queues while offline);
// pending buys replay by requestId on reconnect (server dedupes, never
// re-charges). In-flight dedupe is by item id for stacking. Only an explicit
// server nack (ack ok:false / error with requestId) rolls back and toasts —
// there is no timeout error path: a missing ack heals silently from the next
// broadcast/snapshot. Every tap resolves visibly: instant optimistic level +
// pending state (spinner/disabled) -> success toast (new level + cost) or
// error toast (need vs have + gate reason). No silent drops.
type PendingBuy = { kind: string; id: string; itemKey: string; cost: number; payload: Record<string, unknown>; prevCredits: string; prevCookies: string; prevPower: string; prevUpgrades: Record<string, number>; prevModules: Snapshot["modules"] | null; prevProjects: Snapshot["projects"] | null; prevSpec: string; prevCosmetics?: Snapshot["cosmetics"] | null; prevEquipped?: import("./types").SnapshotLoadout | null }
type BakeryStoreValue = {
  snapshot: Snapshot | null
  snap: Snapshot | null
  connected: boolean
  error: string | null
  buyNotice: BuyNotice | null
  clearBuyNotice: () => void
  isPending: (kind: string, id: string) => boolean
  pendingCount: number
  purchaseHistory: PurchaseRecord[]
  click: (x: number, y: number) => number
  clickSneaky: (id: string, x: number, y: number) => void
  clickMini: (id: string, x: number, y: number) => void
  redeemGolden: (x: number, y: number) => void
  buyUpgrade: (id: string) => void
  buyModule: (id: string) => void
  buyProject: (id: string) => void
  buyCosmetic: (id: string) => void
  equipCosmetic: (id: string, slot?: string) => void
  chooseSpec: (id: string) => void
  activateAbility: (id: string) => void
  claimQuest: (id: string) => void
  shownCookies: number
  liveCredits: number
  cookiesPerClick: number
  creditsPerClick: number
  clickBreakdown: string
  basePower: number
  retry: () => void
  refresh: () => Promise<void>
  quests: StoreQuest[]
  claimableCount: number
  presence: Snapshot["players"]
  offlineBonus: Snapshot["offlineBonus"] | null
  clearOfflineBonus: () => void
  sneakyFlash: SneakyFlash | null
  miniFlash: MiniFlash | null
  goldenFlash: GoldenFlash | null
  subscribe: (handler: (e: ServerEvent) => void) => () => void
  userId: string
}

const BakeryStoreContext = createContext<BakeryStoreValue | null>(null)

export function useBakeryStore(): BakeryStoreValue {
  const v = useContext(BakeryStoreContext)
  if (!v) throw new Error("useBakeryStore must be used within BakeryStoreProvider")
  return v
}

export function BakeryStoreProvider({ guildId, token, userId, enabled, children, onEvent }: { guildId: string | null; token: string; userId: string; enabled: boolean; children: React.ReactNode; onEvent?: (e: ServerEvent) => void }) {
  const [snap, setSnap] = useState<Snapshot | null>(null)
  const [connected, setConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [optimistic, setOptimistic] = useState(0)
  const [optimisticCredits, setOptimisticCredits] = useState(0)
  const [now, setNow] = useState(0)
  useEffect(() => { setNow(Date.now()) }, [])
  const [offlineBonus, setOfflineBonus] = useState<Snapshot["offlineBonus"] | null>(null)
  const [sneakyFlash, setSneakyFlash] = useState<SneakyFlash | null>(null)
  const [miniFlash, setMiniFlash] = useState<MiniFlash | null>(null)
  const [goldenFlash, setGoldenFlash] = useState<GoldenFlash | null>(null)
  const [buyNotice, setBuyNotice] = useState<BuyNotice | null>(null)
  const [purchaseHistory, setPurchaseHistory] = useState<PurchaseRecord[]>([])
  const [pendingKeys, setPendingKeys] = useState<string[]>([])
  const purchaseKeyRef = useRef(1)
  const pendingBuysRef = useRef<Map<string, PendingBuy>>(new Map())
  const noticeIdRef = useRef(1)
  const connRef = useRef<GameConnection | null>(null)
  const powerRef = useRef(1)
  const snapRef = useRef<Snapshot | null>(null)
  const pendingRef = useRef<number[]>([])
  const pendingCreditsRef = useRef<number[]>([])
  const pendingTimersRef = useRef<number[]>([])
  const fxRef = useRef(onEvent)
  const handlersRef = useRef<Set<(e: ServerEvent) => void>>(new Set())
  const creatingRetryDone = useRef(false)
  const lastSeqRef = useRef(0)
  const lastResyncRef = useRef(0)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimerRef = useRef<number | null>(null)
  const initialFetchGuildRef = useRef("")
  const mountedRef = useRef(false)
  // Live-balance refs: buy validation must agree with the displayed
  // shownCookies/liveCredits (bank + elapsed*CPS + optimistic), never the
  // stale snapshot bank. Synced every render below; callbacks read .current.
  const shownCookiesRef = useRef(0)
  const liveCreditsRef = useRef(0)
  // Raw CPS from main cookie pointerdown (every tap, throttled tester_cps send).
  // Global so bakery view contributes to CPS board even without opening tester page.
  const rawTesterTapsRef = useRef<number[]>([])
  const rawTesterPeakRef = useRef(0)
  const rawTesterCpsRef = useRef(0)
  const lastRawTesterSentRef = useRef(0)

  // Live bank estimate for a snapshot: settled bank + whole-second CPS
  // accrual (mirrors server settleProduction floor) + unacked click cookies.
  // Used by buy validation so CPS earned since the last snapshot counts.
  const liveBankFor = useCallback((s: Snapshot): bigint => sharedLiveBankFor(s, pendingRef.current), [])

  useEffect(() => {
    fxRef.current = onEvent
  }, [onEvent])

  const subscribe = useCallback((handler: (e: ServerEvent) => void) => {
    handlersRef.current.add(handler)
    return () => {
      handlersRef.current.delete(handler)
    }
  }, [])

  const emitFx = useCallback((e: ServerEvent) => {
    try {
      fxRef.current?.(e)
    } catch {
      return
    }
    for (const h of handlersRef.current) {
      try {
        h(e)
      } catch {
        return
      }
    }
  }, [])

  useEffect(() => {
    snapRef.current = snap
  }, [snap])

  const cleanGuildId = typeof guildId === "string" ? guildId.trim() : ""
  const guildValid = cleanGuildId !== "" && isSnowflake(cleanGuildId)
  const ready = enabled && guildValid && !!token && !!userId

  useEffect(() => {
    if (!ready) return
    const t = window.setInterval(() => setNow(Date.now()), BAKERY_LIVE_TICK_MS)
    return () => window.clearInterval(t)
  }, [ready])

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
  }, [])

  const pushNotice = useCallback((text: string, kind: string, ok = false) => {
    const id = noticeIdRef.current++
    setBuyNotice({ id, text, kind, ok })
    window.setTimeout(() => setBuyNotice((cur) => (cur !== null && cur.id === id ? null : cur)), ok ? NOTICE_OK_MS : NOTICE_ERR_MS)
  }, [])

  const clearBuyNotice = useCallback(() => setBuyNotice(null), [])

  const recordPurchase = useCallback((kind: string, id: string, label: string, level?: number, cost?: string) => {
    const key = purchaseKeyRef.current++
    setPurchaseHistory((prev) => [{ key, kind, id, label, level, cost, at: Date.now() }, ...prev].slice(0, 8))
  }, [])

  // Reactive pending keys for spinner/disabled UX. pendingBuysRef is the
  // source of truth; this mirrors its itemKeys into state so cards can
  // render "Buying…" with the disabled reason instead of going silent.
  const syncPendingKey = useCallback((kind: string, id: string) => {
    const key = kind + ":" + id.toLowerCase()
    const still = (() => {
      for (const p of pendingBuysRef.current.values()) {
        if (p.itemKey === key) return true
      }
      return false
    })()
    setPendingKeys((prev) => {
      const has = prev.includes(key)
      if (still && !has) return [...prev, key]
      if (!still && has) return prev.filter((k) => k !== key)
      return prev
    })
  }, [])

  const isPending = useCallback((kind: string, id: string): boolean => {
    return pendingKeys.includes(kind + ":" + id.toLowerCase())
  }, [pendingKeys])

  const inflightBuyItem = useCallback((kind: string, id: string): boolean => {
    const key = kind + ":" + id.toLowerCase()
    for (const p of pendingBuysRef.current.values()) {
      if (p.itemKey === key) return true
    }
    return false
  }, [])

  // Number of in-flight purchases for the same item (same kind+id, any
  // requestId). Consecutive buys of upgrades/modules stack: the next buy
  // prices level+pendingCount, never the stale settled level.
  const pendingCountFor = useCallback((kind: string, id: string, excludeRequestId?: string): number => {
    const key = kind + ":" + id.toLowerCase()
    let n = 0
    for (const [rid, p] of pendingBuysRef.current) {
      if (p.itemKey === key && rid !== excludeRequestId) n += 1
    }
    return n
  }, [])

  const clearBuyTimer = useCallback((_requestId: string) => {
    // No-op: buys have no timeout timer. Pending resolves only via explicit
    // server ack/nack or heal from broadcast/snapshot. Kept so call-sites
    // stay untouched.
  }, [])

  // Roll the instant-optimistic apply back to pre-tap state on explicit
  // server nack only (there is no timeout path).
  // Stacking-aware: when sibling in-flight buys for the same item exist,
  // only this buy's own delta is refunded (level -1, cost refunded, catalog
  // cost stepped back) instead of wiping the siblings' optimistic levels.
  // Failed rolls back exactly the failed buy's own delta; siblings keep
  // their optimistic levels. Pending spinner clears only when no sibling
  // for the same item remains in flight.
  const rollbackBuy = useCallback((requestId: string) => {
    const pend = pendingBuysRef.current.get(requestId)
    if (!pend) return
    pendingBuysRef.current.delete(requestId)
    const siblings = pendingCountFor(pend.kind, pend.id)
    const doneSync = () => syncPendingKey(pend.kind, pend.id)
    if (siblings > 0) {
      const refund = pend.cost
      setSnap((s) => {
        if (!s) return s
        if (pend.kind === "upgrade") {
          const curLv = s.me.upgrades?.[pend.id] ?? 0
          const nextLv = Math.max(0, curLv - 1)
          let nextCredits = s.me.credits
          try {
            nextCredits = (BigInt(s.me.credits) + BigInt(Math.floor(refund))).toString()
          } catch {
            nextCredits = String(toNum(s.me.credits) + refund)
          }
          const nextUpgrades = { ...(s.me.upgrades ?? {}), [pend.id]: nextLv }
          let next = { ...s, me: { ...s.me, credits: nextCredits, upgrades: nextUpgrades } }
          if (next.upgrades) {
            const def = upgradeDefFor(pend.id)
            next = {
              ...next,
              upgrades: next.upgrades.map((u) => {
                if (u.id !== pend.id) return u
                const lv = Math.max(0, (u.level ?? 0) - 1)
                let cost = u.cost
                // Step the catalog cost back one level: recompute exactly
                // from the stepped-back level when the def is known.
                if (def) {
                  try {
                    cost = String(costFor(def, lv))
                  } catch {
                    cost = u.cost
                  }
                }
                return { ...u, level: lv, cost }
              }),
            }
          }
          return next
        }
        if (pend.kind === "module") {
          let nextCookies = s.bakery.cookies
          try {
            nextCookies = (BigInt(s.bakery.cookies) + BigInt(Math.floor(refund))).toString()
          } catch {
            nextCookies = String(toNum(s.bakery.cookies) + refund)
          }
          let next = { ...s, bakery: { ...s.bakery, cookies: nextCookies } }
          if (next.modules) {
            next = {
              ...next,
              modules: next.modules.map((x) => {
                if (x.id !== pend.id) return x
                const lv = Math.max(0, x.level - 1)
                // No frontend module base costs: step back by reversing one
                // 1.5x escalation from the current row cost.
                let cost = x.cost
                try {
                  const cur = BigInt(x.cost)
                  cost = ((cur * 2n) / 3n).toString()
                } catch {
                  cost = x.cost
                }
                return { ...x, level: lv, cost }
              }),
            }
          }
          return next
        }
        return s
      })
      doneSync()
      return
    }
    const prevUp = pend.prevUpgrades
    const prevMod = pend.prevModules
    const prevProj = pend.prevProjects
    const prevSpec = pend.prevSpec
    const prevCos = pend.prevCosmetics
    const prevEq = pend.prevEquipped
    setSnap((s) => {
      if (!s) return s
      let next = s
      if (pend.kind === "upgrade") {
        next = { ...next, me: { ...next.me, credits: pend.prevCredits, upgrades: { ...prevUp } } }
      } else if (pend.kind === "module") {
        next = {
          ...next,
          bakery: { ...next.bakery, cookies: pend.prevCookies },
          ...(prevMod ? { modules: prevMod.map((x) => ({ ...x })) } : {}),
        }
      } else if (pend.kind === "cosmetic") {
        next = {
          ...next,
          bakery: { ...next.bakery, cookies: pend.prevCookies },
          me: {
            ...next.me,
            credits: pend.prevCredits,
            ...(next.me.cosmetics ? { cosmetics: { owned: (next.me.cosmetics.owned ?? []).filter((x) => x !== pend.id), equipped: prevEq ? { ...prevEq } : { ...next.me.cosmetics.equipped } } } : {}),
          },
          ...(prevCos ? { cosmetics: prevCos.map((x) => ({ ...x })) } : {}),
        }
      } else if (pend.kind === "equip") {
        next = {
          ...next,
          me: {
            ...next.me,
            ...(next.me.cosmetics && prevEq ? { cosmetics: { owned: [...next.me.cosmetics.owned], equipped: { ...prevEq } } } : {}),
          },
          ...(prevCos ? { cosmetics: prevCos.map((x) => ({ ...x })) } : {}),
        }
      } else if (pend.kind === "project") {
        next = {
          ...next,
          bakery: { ...next.bakery, cookies: pend.prevCookies },
          me: { ...next.me, credits: pend.prevCredits },
          ...(prevProj ? { projects: prevProj.map((x) => ({ ...x })) } : {}),
        }
      } else if (pend.kind === "spec") {
        next = { ...next, me: { ...next.me, specialization: prevSpec } }
      }
      return next
    })
    doneSync()
  }, [pendingCountFor, syncPendingKey])

  // Fire-and-forget send: GameConnection queues while the socket is down, so
  // a send here never fails the purchase. Kept as a helper so every buy
  // path shares the same no-throw contract (only explicit server nacks
  // roll back / toast).
  const fireBuy = useCallback((payload: Record<string, unknown>) => {
    const c = connRef.current
    if (c === null) return
    try {
      c.send({ ...payload } as unknown as never)
    } catch {
      // Queued for reconnect replay below; optimistic state already applied.
    }
  }, [])

  // Background retry queue: on reconnect, replay every still-pending buy
  // with its ORIGINAL requestId (server dedupes by requestId, never
  // re-charges). Stale-ack safe: acks are always accepted and broadcasts
  // heal idempotently.
  const replayPendingBuys = useCallback(() => {
    if (pendingBuysRef.current.size === 0) return
    const c = connRef.current
    if (c === null) return
    for (const pend of pendingBuysRef.current.values()) {
      try {
        c.send({ ...pend.payload } as unknown as never)
      } catch {
        // Next reconnect retries again; no toast on transport silence.
      }
    }
  }, [])

  // Broadcast heal: a purchase broadcast for our own user confirms any
  // still-pending buy for that item (covers a lost ack). Idempotent: if the
  // ack already cleared the pending, this is a no-op. Shows the success
  // confirmation only when the pending was still open (no double toast).
  const confirmPendingFromBroadcast = useCallback((kind: string, id: string, level?: number, cost?: string, personalCost?: string) => {
    let matched: string | null = null
    for (const [rid, p] of pendingBuysRef.current) {
      if (p.itemKey === kind + ":" + id.toLowerCase()) {
        matched = rid
        break
      }
    }
    if (matched === null) return
    const pend = pendingBuysRef.current.get(matched)
    pendingBuysRef.current.delete(matched)
    syncPendingKey(kind, id)
    if (pend !== undefined && (kind === "upgrade" || kind === "module" || kind === "project" || kind === "ability" || kind === "cosmetic" || kind === "equip")) {
      if (kind === "equip") {
        const cleared = id === "" || id.startsWith("none")
        const rawId = cleared ? "" : id
        const label = cleared ? "" : displayNameFor("equip", id)
        pushNotice(buySuccessText("equip", label, undefined, undefined), "equip", true)
        recordPurchase("equip", cleared ? id : rawId, cleared ? "Unequip" : label, undefined, undefined)
        try {
          sfxBuy()
        } catch {
          // audio is best-effort; the toast + ledger already confirm
        }
        return
      }
      if (kind === "cosmetic") {
        const label = displayNameFor("cosmetic", id)
        const charged = cost ?? (pend.cost > 0 ? String(Math.floor(pend.cost)) : undefined)
        const currency = snapRef.current?.cosmetics?.find((x) => x.id === id)?.currency
          ?? COSMETICS.find((c) => c.id === id)?.currency
          ?? "credits"
        const coin = currency === "cookies" ? "🍪" : "🪙"
        pushNotice(charged !== undefined ? `✅ ${label} owned (${coin} ${fmtCompact(charged)}).` : `✅ ${label} owned.`, "cosmetic", true)
        recordPurchase("cosmetic", id, label, undefined, charged)
        try {
          sfxBuy()
        } catch {
          // audio is best-effort; the toast + ledger already confirm
        }
        return
      }
      const label = displayNameFor(kind, id)
      const lvl = typeof level === "number" && level > 0 ? level : undefined
      const charged = cost ?? (pend.cost > 0 ? String(Math.floor(pend.cost)) : undefined)
      // Personal stake for projects: prefer broadcast fee, else derive 10%.
      let personal: string | undefined
      if (kind === "project") {
        personal = personalCost
        if (personal === undefined) {
          const row = snapRef.current?.projects?.find((x) => x.id.toLowerCase() === id.toLowerCase())
          if (row !== undefined) personal = projectPersonalFeeStr(row.cookieCost, row.personalCost)
          else if (charged !== undefined) personal = projectPersonalFeeStr(charged, undefined)
        }
      }
      pushNotice(buySuccessText(kind, label, lvl, charged, personal), kind === "project" ? "project" : kind, true)
      recordPurchase(kind, id, label, lvl, charged)
      try {
        if (kind === "ability") sfxAbility()
        else sfxBuy()
      } catch {
        // audio is best-effort; the toast + ledger already confirm
      }
    }
  }, [pushNotice, recordPurchase, syncPendingKey])

  // Snapshot heal: if the authoritative snapshot already reflects a pending
  // purchase, clear the pending spinner silently (optimistic state already
  // shows it; ack/broadcast toasts if they arrive). Never toasts or rolls
  // back — snapshots are the heal path, not an error path.
  const healPendingFromSnapshot = useCallback((incoming: Snapshot) => {
    if (pendingBuysRef.current.size === 0) return
    for (const [rid, pend] of [...pendingBuysRef.current]) {
      if (pend.kind === "upgrade") {
        const prevLv = pend.prevUpgrades[pend.id] ?? 0
        const curLv = incoming.me.upgrades?.[pend.id] ?? prevLv
        if (curLv > prevLv) {
          pendingBuysRef.current.delete(rid)
          syncPendingKey(pend.kind, pend.id)
        }
      } else if (pend.kind === "module") {
        const prevLv = pend.prevModules?.find((x) => x.id === pend.id)?.level ?? 0
        const curLv = incoming.modules?.find((x) => x.id === pend.id)?.level ?? prevLv
        if (curLv > prevLv) {
          pendingBuysRef.current.delete(rid)
          syncPendingKey(pend.kind, pend.id)
        }
      } else if (pend.kind === "project") {
        const st = incoming.projects?.find((x) => x.id === pend.id)?.status
        if (st === "in_progress" || st === "completed") {
          pendingBuysRef.current.delete(rid)
          syncPendingKey(pend.kind, pend.id)
        }
      } else if (pend.kind === "quest") {
        if (incoming.quests?.find((x) => x.id === pend.id)?.claimed === true) {
          pendingBuysRef.current.delete(rid)
          syncPendingKey(pend.kind, pend.id)
        }
      } else if (pend.kind === "spec") {
        if ((incoming.me.specialization ?? "").toLowerCase() === pend.id.toLowerCase()) {
          pendingBuysRef.current.delete(rid)
          syncPendingKey(pend.kind, pend.id)
        }
      } else if (pend.kind === "ability") {
        const row = incoming.abilities?.find((x) => x.id === pend.id)
        if (row !== undefined && (row.cooldownRemainingMs > 0 || row.activeRemainingMs > 0)) {
          pendingBuysRef.current.delete(rid)
          syncPendingKey(pend.kind, pend.id)
        }
      } else if (pend.kind === "cosmetic") {
        if ((incoming.me.cosmetics?.owned ?? []).includes(pend.id)) {
          pendingBuysRef.current.delete(rid)
          syncPendingKey(pend.kind, pend.id)
        }
      } else if (pend.kind === "equip") {
        const eq = incoming.me.cosmetics?.equipped
        const slot = (pend.payload["slot"] as string | undefined) ?? ""
        const want = pend.id.startsWith("none") ? "" : pend.id
        if (eq && (slot === "" ? eq.skin === want || eq.glow === want || eq.ring === want || eq.trail === want || eq.sound === want : (eq as unknown as Record<string, string>)[slot] === want)) {
          pendingBuysRef.current.delete(rid)
          syncPendingKey(pend.kind, pend.id)
        }
      }
    }
  }, [syncPendingKey])

  const lastQuestSyncRef = useRef(0)
  const requestResync = useCallback(() => {
    const nowMs = Date.now()
    if (nowMs - lastResyncRef.current < API_RESYNC_THROTTLE_MS) return
    lastResyncRef.current = nowMs
    const c = connRef.current
    if (c === null) return
    try {
      c.send({ type: "resync" } as unknown as never)
    } catch {
      void 0
    }
  }, [])

  const requestQuestSync = useCallback(() => {
    const nowMs = Date.now()
    if (nowMs - lastQuestSyncRef.current < QUEST_SYNC_THROTTLE_MS) return
    lastQuestSyncRef.current = nowMs
    lastResyncRef.current = nowMs
    const c = connRef.current
    if (c === null) return
    try {
      c.send({ type: "resync" } as unknown as never)
    } catch {
      void 0
    }
  }, [])

  const scheduleReconnect = useCallback(() => {
    if (!ready) return
    clearReconnectTimer()
    const attemptNum = reconnectAttemptsRef.current
    const delay = Math.min(1000 * (1 << Math.min(attemptNum, 3)), API_BACKOFF_MAX_MS)
    reconnectAttemptsRef.current = Math.min(attemptNum + 1, 10)
    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null
      const c = connRef.current
      if (c !== null) {
        try {
          c.send({ type: "hello" } as unknown as never)
        } catch {
          void 0
        }
      }
      setAttempt((a) => a + 1)
    }, delay)
  }, [ready, clearReconnectTimer])

  const handleSeq = useCallback((seq: number): boolean => {
    if (!Number.isFinite(seq) || seq <= 0) return true
    const last = lastSeqRef.current
    // Never drop live events on seq gaps/duplicates: gap triggers a
    // background resync while the event still applies (all purchase merges
    // are idempotent via max-level / absolute balances). Healing comes from
    // broadcast/snapshot, never from a timeout toast.
    if (seq <= last) return true
    if (last !== 0 && hasSeqGap(last, seq)) {
      lastSeqRef.current = Math.max(last, seq)
      requestResync()
      return true
    }
    lastSeqRef.current = Math.max(last, seq)
    return true
  }, [requestResync])

  useEffect(() => {
    if (!ready) return
    mountedRef.current = true
    let off: (() => void) | null = null
    const acquired = acquireSharedConnection({ guildId: cleanGuildId, token, userId })
    const conn = acquired.conn
    const sharedKey = acquired.key
    connRef.current = conn
    off = conn.onEvent((e) => {
      if (!mountedRef.current) return
      if (e.type === "status") {
        setConnected(e.connected)
        if (e.connected) {
          setError(null)
          clearReconnectTimer()
          reconnectAttemptsRef.current = 0
          requestResync()
          // Reconnect replay: re-send still-pending buys with their original
          // requestIds (server dedupes). No toast on transport silence.
          replayPendingBuys()
        } else {
          scheduleReconnect()
        }
        return
      }
      if (e.type === "error") {
        const rid = (e as unknown as { requestId?: string }).requestId
        if (typeof rid === "string" && rid !== "") {
          const pend = pendingBuysRef.current.get(rid)
          if (pend !== undefined) {
            const err = e as unknown as { reason?: string; need?: string; have?: string; level?: number; cooldownRemainingMs?: number }
            const reason = err.reason ?? e.message
            rollbackBuy(rid)
            const rc = reason.trim().toLowerCase()
            const isCookies = rc === "insufficient_cookies"
            const fallbackHave = isCookies ? shownCookiesRef.current : rc === "insufficient_credits" ? liveCreditsRef.current : undefined
            pushNotice(buyFailureText({ reason, need: err.need, have: err.have, level: err.level, cooldownRemainingMs: err.cooldownRemainingMs }, pend.cost > 0 ? pend.cost : undefined, fallbackHave), pend.kind)
            // No full snapshot refetch on buy: rollback + ack/broadcast deltas
            // are authoritative. Next live event heals any residual drift.
          } else {
            pushNotice(friendlyBuyError((e as unknown as { reason?: string }).reason ?? e.message), "buy")
          }
          emitFx(e)
          return
        }
        if (isJoinRelatedError(e.message)) {
          const mapped = friendlyJoinError(e.message)
          if (mapped !== null) setError(mapped)
        } else {
          pushNotice(friendlyBuyError(e.message), "buy")
          emitFx(e)
        }
        return
      }
      if ((e as unknown as { type: string }).type === "ack") {
        const ack = e as unknown as { requestId: string; ok: boolean; reason: string; upgradeId?: string; moduleId?: string; projectId?: string; questId?: string; abilityId?: string; cosmeticId?: string; slot?: string; currency?: string; specId?: string; cost?: string; personalCost?: string; need?: string; have?: string; maxLevel?: number; reward?: number; credits?: string; cookies?: string; clickPower?: string; cps?: string; level: number; progress?: number; target?: number; startedAt?: number; endsAt?: number; durationMs?: number; cooldownMs?: number; cooldownRemainingMs?: number; bakeryCookies?: string; bakeryCps?: string; bakeryLifetimeCookies?: string; bakeryLevel?: number; bakeryNextLevelCookies?: string; bakeryLevelProgress?: number }
        if (typeof ack.requestId === "string" && ack.requestId !== "") {
          const pend = pendingBuysRef.current.get(ack.requestId)
          if (pend !== undefined) {
            if (!ack.ok) {
              rollbackBuy(ack.requestId)
              // Re-apply server progress for rejected claims so the Claim
              // button fixes itself instantly (no refetch needed).
              if (pend.kind === "quest" && typeof ack.progress === "number" && ack.questId) {
                const qid = ack.questId
                const prog = ack.progress
                const targ = typeof ack.target === "number" ? ack.target : undefined
                setSnap((s) => {
                  if (!s || !s.quests) return s
                  return { ...s, quests: s.quests.map((x) => x.id === qid ? { ...x, progress: prog, ...(targ !== undefined ? { target: targ } : {}) } : x) }
                })
              }
              const ackReason = (ack.reason ?? "").trim().toLowerCase()
              const isCookies = ackReason === "insufficient_cookies"
              const fallbackHave = ackReason === "insufficient_cookies" || ackReason === "insufficient_credits"
                ? Math.max(0, Math.floor(isCookies ? shownCookiesRef.current : liveCreditsRef.current))
                : undefined
              // Server need/have (authoritative BigInt strings) win; fall back
              // to the optimistic charge + live balance for legacy nacks.
              pushNotice(buyFailureText({ reason: ack.reason, need: ack.need, have: ack.have, level: ack.level, cooldownRemainingMs: ack.cooldownRemainingMs }, pend.cost > 0 ? pend.cost : undefined, fallbackHave), pend.kind)
              // Note: the next live event/snapshot heals the displayed bank.
            } else {
              // Authoritative ack state wins (BigInt strings). No refetch:
              // the ack + live broadcast deltas carry everything. Kind-aware
              // merge without mixing: credits/clickPower/personal-cps stay on
              // me/players, bank/lifetime/cps stay on bakery.
              clearBuyTimer(ack.requestId)
              pendingBuysRef.current.delete(ack.requestId)
              setSnap((s) => {
                if (!s) return s
                let next = s
                const applyBakeryTrio = (bank?: string, cps?: string, lifetime?: string, level?: number, nextCookies?: string, progress?: number) => {
                  if (bank === undefined && cps === undefined && lifetime === undefined && level === undefined && nextCookies === undefined && progress === undefined) return
                  next = {
                    ...next,
                    bakery: {
                      ...next.bakery,
                      ...(bank !== undefined ? { cookies: bank } : {}),
                      ...(cps !== undefined ? { cps } : {}),
                      ...(lifetime !== undefined ? { lifetimeCookies: lifetime } : {}),
                      ...(level !== undefined ? { level } : {}),
                      ...(nextCookies !== undefined ? { nextLevelCookies: nextCookies } : {}),
                      ...(progress !== undefined ? { levelProgress: progress } : {}),
                    },
                  }
                }
                if (pend.kind === "upgrade") {
                  // Upgrade spends Baker Credits (me), never bank cookies.
                  // ack.cps here is personal automation CPS -> players entry.
                  if (ack.credits !== undefined) next = { ...next, me: { ...next.me, credits: ack.credits } }
                  if (ack.clickPower !== undefined) next = { ...next, me: { ...next.me, clickPower: ack.clickPower } }
                  if (ack.bakeryCookies !== undefined || ack.bakeryCps !== undefined || ack.bakeryLifetimeCookies !== undefined || ack.bakeryLevel !== undefined) {
                    applyBakeryTrio(ack.bakeryCookies, ack.bakeryCps, ack.bakeryLifetimeCookies, ack.bakeryLevel, ack.bakeryNextLevelCookies, ack.bakeryLevelProgress)
                  }
                  if (ack.cps !== undefined) {
                    const personalCps = ack.cps
                    next = { ...next, players: next.players.map((p) => p.userId === userId ? { ...p, cps: personalCps, active: true } : p) }
                  }
                } else if (pend.kind === "module") {
                  // Module spends bank cookies; ack carries the
                  // authoritative bank trio together.
                  const bank = ack.bakeryCookies ?? ack.cookies
                  const cps = ack.bakeryCps ?? ack.cps
                  const lifetime = ack.bakeryLifetimeCookies
                  applyBakeryTrio(bank, cps, lifetime, ack.bakeryLevel, ack.bakeryNextLevelCookies, ack.bakeryLevelProgress)
                } else if (pend.kind === "project") {
                  // Project spends bank cookies + personal Baker Credits;
                  // ack carries the authoritative bank trio + credits.
                  const bank = ack.bakeryCookies ?? ack.cookies
                  const cps = ack.bakeryCps ?? ack.cps
                  const lifetime = ack.bakeryLifetimeCookies
                  applyBakeryTrio(bank, cps, lifetime, ack.bakeryLevel, ack.bakeryNextLevelCookies, ack.bakeryLevelProgress)
                  if (ack.credits !== undefined) next = { ...next, me: { ...next.me, credits: ack.credits } }
                } else if (pend.kind === "quest") {
                  if (ack.credits !== undefined) next = { ...next, me: { ...next.me, credits: ack.credits } }
                } else if (pend.kind === "ability") {
                  // Abilities spend Baker Credits (me); the ack carries the
                  // authoritative credits + bakery trio (emergency_bake adds
                  // bank cookies). Cooldown/active timers merge below.
                  if (ack.credits !== undefined) next = { ...next, me: { ...next.me, credits: ack.credits } }
                  applyBakeryTrio(ack.bakeryCookies, ack.bakeryCps, ack.bakeryLifetimeCookies, ack.bakeryLevel, ack.bakeryNextLevelCookies, ack.bakeryLevelProgress)
                  if (ack.abilityId && next.abilities) {
                    const aid = ack.abilityId
                    const nowMs = Date.now()
                    next = {
                      ...next,
                      abilities: next.abilities.map((x) => {
                        if (x.id !== aid) return x
                        const cd = typeof ack.cooldownMs === "number" ? ack.cooldownMs : x.cooldownMs
                        const activeMs = typeof ack.endsAt === "number" ? Math.max(0, ack.endsAt - nowMs) : x.activeRemainingMs
                        return { ...x, cooldownMs: cd, cooldownRemainingMs: cd, activeRemainingMs: activeMs, endsAt: typeof ack.endsAt === "number" ? ack.endsAt : x.endsAt }
                      }),
                    }
                  }
                } else if (pend.kind === "cosmetic") {
                  // Cosmetic buy spends one currency only (credits on me, or
                  // bank cookies for the golden skin); the ack carries the
                  // authoritative balances. Ownership merges below.
                  if (ack.credits !== undefined) next = { ...next, me: { ...next.me, credits: ack.credits } }
                  const bank = ack.bakeryCookies ?? ack.cookies
                  applyBakeryTrio(bank, ack.bakeryCps, ack.bakeryLifetimeCookies, ack.bakeryLevel, ack.bakeryNextLevelCookies, ack.bakeryLevelProgress)
                  const cid = ack.cosmeticId ?? pend.id
                  if (next.me.cosmetics && !next.me.cosmetics.owned.includes(cid)) {
                    next = { ...next, me: { ...next.me, cosmetics: { owned: [...next.me.cosmetics.owned, cid], equipped: { ...next.me.cosmetics.equipped } } } }
                  }
                  if (next.cosmetics) {
                    next = { ...next, cosmetics: next.cosmetics.map((x) => x.id === cid ? { ...x, owned: true } : x) }
                  }
                } else if (pend.kind === "equip") {
                  // Equip changes no balances; the ack is authoritative for the
                  // loadout (not just optimistic). Apply it to both
                  // me.cosmetics.equipped and the catalog so glow/ring/trail
                  // survive a racing snapshot that landed between optimistic
                  // apply and ack (skin survived because BakeScene reads the
                  // same loadout, but only if the ack re-asserts it).
                  const cid = ack.cosmeticId ?? pend.id
                  const slot = ack.slot ?? (pend.payload["slot"] as string | undefined) ?? ""
                  const cleared = cid === "" || cid.startsWith("none")
                  const applyId = cleared ? "" : cid
                  if (slot === "skin" || slot === "glow" || slot === "ring" || slot === "trail" || slot === "sound") {
                    if (next.me.cosmetics) {
                      next = { ...next, me: { ...next.me, cosmetics: { owned: [...next.me.cosmetics.owned], equipped: { ...next.me.cosmetics.equipped, [slot]: applyId } } } }
                    }
                    if (next.cosmetics) {
                      next = {
                        ...next,
                        cosmetics: next.cosmetics.map((x) => {
                          if (x.kind !== slot) return x
                          return { ...x, equipped: x.id === applyId }
                        }),
                      }
                    }
                  } else if (next.cosmetics && slot !== "") {
                    next = {
                      ...next,
                      cosmetics: next.cosmetics.map((x) => {
                        if (x.kind !== slot) return x
                        return { ...x, equipped: x.id === cid }
                      }),
                    }
                  }
                } else {
                  if (ack.credits !== undefined) next = { ...next, me: { ...next.me, credits: ack.credits } }
                  if (ack.cookies !== undefined) next = { ...next, bakery: { ...next.bakery, cookies: ack.cookies } }
                  if (ack.clickPower !== undefined) next = { ...next, me: { ...next.me, clickPower: ack.clickPower } }
                  if (ack.cps !== undefined) next = { ...next, bakery: { ...next.bakery, cps: ack.cps } }
                  applyBakeryTrio(ack.bakeryCookies, ack.bakeryCps, ack.bakeryLifetimeCookies, ack.bakeryLevel, ack.bakeryNextLevelCookies, ack.bakeryLevelProgress)
                }
                if (typeof ack.level === "number" && ack.level > 0) {
                  if (ack.upgradeId && pend.kind === "upgrade") {
                    const cur = next.me.upgrades?.[ack.upgradeId] ?? 0
                    if (ack.level > cur) next = { ...next, me: { ...next.me, upgrades: { ...(next.me.upgrades ?? {}), [ack.upgradeId]: ack.level } } }
                    // Fresh catalog cost from the authoritative ack level so
                    // the next consecutive buy prices the next level.
                    if (next.upgrades) {
                      const lvl = Math.max(ack.level, next.me.upgrades?.[ack.upgradeId] ?? ack.level)
                      next = { ...next, upgrades: next.upgrades.map((u) => u.id === ack.upgradeId ? { ...u, level: Math.max(u.level, lvl), cost: nextUpgradeCostStr(ack.upgradeId as string, Math.max(u.level, lvl), ack.cost) } : u) }
                    }
                  }
                  if (ack.moduleId && pend.kind === "module" && next.modules) {
                    const mid = ack.moduleId
                    next = {
                      ...next,
                      modules: next.modules.map((x) => {
                        if (x.id !== mid) return x
                        const lvl = Math.max(x.level, ack.level)
                        // ack.cost is the charged cost for ack.level - 1, so
                        // the next price is one escalation step up. Without
                        // it, step forward from the row cost for any gap.
                        const cost = ack.cost !== undefined ? escalateCostStr(ack.cost, 1) : lvl > x.level ? escalateCostStr(x.cost, lvl - x.level) : x.cost
                        return { ...x, level: lvl, cost }
                      }),
                    }
                  }
                  if (ack.questId && pend.kind === "quest" && next.quests) {
                    next = { ...next, quests: next.quests.map((x) => x.id === ack.questId ? { ...x, claimed: true } : x) }
                  }
                } else if (ack.questId && pend.kind === "quest" && next.quests) {
                  next = { ...next, quests: next.quests.map((x) => x.id === ack.questId ? { ...x, claimed: true } : x) }
                }
                if (ack.projectId && pend.kind === "project" && next.projects) {
                  const startedAt = typeof ack.startedAt === "number" ? ack.startedAt : Date.now()
                  const durationMs = typeof ack.durationMs === "number" ? ack.durationMs : 0
                  const endsAt = typeof ack.endsAt === "number" ? ack.endsAt : startedAt + durationMs
                  next = { ...next, projects: next.projects.map((x) => x.id === ack.projectId ? { ...x, status: "in_progress", progress: "0", startedAt, endsAt, durationMs, progressPct: 0, timeLeftMs: Math.max(0, endsAt - Date.now()) } : x) }
                }
                if (ack.specId && pend.kind === "spec") {
                  next = { ...next, me: { ...next.me, specialization: ack.specId } }
                }
                return next
              })
              // Ack merges authoritative level/cost/balances + catalog rows
              // above; now confirm visibly. Broadcast deltas update other
              // players via upgrade_purchased/module_upgraded/project_started.
              syncPendingKey(pend.kind, pend.id)
              if (pend.kind === "cosmetic") {
                const itemId = (ack.cosmeticId ?? pend.id) as string
                const label = displayNameFor("cosmetic", itemId)
                const charged = ack.cost ?? (pend.cost > 0 ? String(Math.floor(pend.cost)) : undefined)
                const currency = ack.currency
                  ?? snapRef.current?.cosmetics?.find((x) => x.id === itemId)?.currency
                  ?? COSMETICS.find((c) => c.id === itemId)?.currency
                  ?? "credits"
                const coin = currency === "cookies" ? "🍪" : "🪙"
                const text = charged !== undefined ? `✅ ${label} owned (${coin} ${fmtCompact(charged)}).` : `✅ ${label} owned.`
                pushNotice(text, "cosmetic", true)
                recordPurchase("cosmetic", itemId, label, undefined, charged)
                try {
                  sfxBuy()
                } catch {
                  // audio is best-effort; the toast + ledger already confirm
                }
              } else if (pend.kind === "equip") {
                const rawEquipId = ack.cosmeticId ?? pend.id
                const cleared = rawEquipId === "" || rawEquipId.startsWith("none")
                const label = cleared ? "" : displayNameFor("equip", rawEquipId)
                pushNotice(buySuccessText("equip", label, undefined, undefined), "equip", true)
                recordPurchase("equip", cleared ? (ack.slot ?? pend.id) : rawEquipId, cleared ? "Unequip" : label, undefined, undefined)
                try {
                  sfxBuy()
                } catch {
                  // audio is best-effort; the toast + ledger already confirm
                }
              } else if (pend.kind === "upgrade" || pend.kind === "module" || pend.kind === "project" || pend.kind === "ability" || pend.kind === "cosmetic") {
                const itemId = (ack.upgradeId ?? ack.moduleId ?? ack.projectId ?? ack.abilityId ?? ack.cosmeticId ?? pend.id) as string
                const label = displayNameFor(pend.kind, itemId)
                const lvl = typeof ack.level === "number" && ack.level > 0 ? ack.level : undefined
                const charged = ack.cost ?? (pend.cost > 0 ? String(Math.floor(pend.cost)) : undefined)
                const ackPersonal = (ack as unknown as Record<string, unknown>)["personalCost"]
                const personal = pend.kind === "project"
                  ? (typeof ackPersonal === "string" && ackPersonal !== "" ? ackPersonal : (() => {
                      const row = snapRef.current?.projects?.find((x) => x.id.toLowerCase() === itemId.toLowerCase())
                      if (row !== undefined) return projectPersonalFeeStr(row.cookieCost, row.personalCost)
                      return charged !== undefined ? projectPersonalFeeStr(charged, undefined) : undefined
                    })())
                  : undefined
                pushNotice(buySuccessText(pend.kind, label, lvl, charged, personal), pend.kind === "project" ? "project" : pend.kind, true)
                recordPurchase(pend.kind, itemId, label, lvl, charged)
                try {
                  if (pend.kind === "ability") sfxAbility()
                  else sfxBuy()
                } catch {
                  // audio is best-effort; the toast + ledger already confirm
                }
              } else if (pend.kind === "quest" && ack.questId) {
                pushNotice(`✅ Quest claimed (+${ack.reward ?? ""} credits).`, "quest", true)
                try {
                  sfxBuy()
                } catch {
                  void 0
                }
              } else if (pend.kind === "spec" && ack.specId) {
                pushNotice(`✅ Specialization: ${ack.specId}.`, "spec", true)
              }
            }
          }
        }
        emitFx(e as unknown as ServerEvent)
        return
      }
      if ((e as unknown as { type: string }).type === "quest_claimed") {
        const qc = e as unknown as { userId: string; questId: string; credits?: string }
        handleSeq((e as unknown as { seq: number }).seq)
        if (qc.userId === userId) confirmPendingFromBroadcast("quest", qc.questId)
        if (qc.credits !== undefined) {
          setSnap((s) => {
            if (!s) return s
            let next = s
            if (qc.credits !== undefined) next = { ...next, me: { ...next.me, credits: qc.credits as string } }
            if (next.quests) next = { ...next, quests: next.quests.map((x) => x.id === qc.questId ? { ...x, claimed: true } : x) }
            return next
          })
        } else {
          requestResync()
        }
        emitFx(e as unknown as ServerEvent)
        return
      }
      if ((e as unknown as { type: string }).type === "cosmetic_purchased") {
        const cp = e as unknown as { seq: number; userId: string; cosmeticId: string; slot: string; cost?: string; currency?: string; credits?: string; bakeryCookies?: string; bakeryCps?: string; bakeryLifetimeCookies?: string; bakeryLevel?: number; bakeryNextLevelCookies?: string; bakeryLevelProgress?: number }
        handleSeq(cp.seq)
        setSnap((s) => {
          if (!s) return s
          let next = s
          if (cp.userId === userId) {
            if (cp.credits !== undefined) next = { ...next, me: { ...next.me, credits: cp.credits } }
            if (cp.bakeryCookies !== undefined || cp.bakeryCps !== undefined || cp.bakeryLifetimeCookies !== undefined || cp.bakeryLevel !== undefined) {
              next = {
                ...next,
                bakery: {
                  ...next.bakery,
                  ...(cp.bakeryCookies !== undefined ? { cookies: cp.bakeryCookies } : {}),
                  ...(cp.bakeryCps !== undefined ? { cps: cp.bakeryCps } : {}),
                  ...(cp.bakeryLifetimeCookies !== undefined ? { lifetimeCookies: cp.bakeryLifetimeCookies } : {}),
                  ...(cp.bakeryLevel !== undefined ? { level: cp.bakeryLevel } : {}),
                },
              }
            }
            if (next.me.cosmetics && !next.me.cosmetics.owned.includes(cp.cosmeticId)) {
              next = { ...next, me: { ...next.me, cosmetics: { owned: [...next.me.cosmetics.owned, cp.cosmeticId], equipped: { ...next.me.cosmetics.equipped } } } }
            }
            if (next.cosmetics) {
              next = { ...next, cosmetics: next.cosmetics.map((x) => x.id === cp.cosmeticId ? { ...x, owned: true } : x) }
            }
            confirmPendingFromBroadcast("cosmetic", cp.cosmeticId, undefined, cp.cost)
          }
          return next
        })
        emitFx(e as unknown as ServerEvent)
        return
      }
      if ((e as unknown as { type: string }).type === "cosmetic_equipped") {
        const ce = e as unknown as { seq: number; userId: string; cosmeticId: string; slot: string; equipped?: import("./types").SnapshotLoadout }
        handleSeq(ce.seq)
        setSnap((s) => {
          if (!s) return s
          const slot = ce.slot
          if (slot !== "skin" && slot !== "glow" && slot !== "ring" && slot !== "trail" && slot !== "sound") return s
          const applyLoadout = (eq: import("./types").SnapshotLoadout): import("./types").SnapshotLoadout =>
            ce.equipped ? { ...ce.equipped } : { ...eq, [slot]: ce.cosmeticId }
          let next = s
          if (ce.userId === userId) {
            if (next.me.cosmetics) {
              next = { ...next, me: { ...next.me, cosmetics: { owned: [...next.me.cosmetics.owned], equipped: applyLoadout(next.me.cosmetics.equipped) } } }
            }
            if (next.cosmetics) {
              next = {
                ...next,
                cosmetics: next.cosmetics.map((x) => {
                  if (x.kind !== slot) return x
                  return { ...x, equipped: x.id === ce.cosmeticId }
                }),
              }
            }
            confirmPendingFromBroadcast("equip", ce.cosmeticId === "" ? "none:" + slot : ce.cosmeticId)
          } else {
            const remoteLoadout = ce.equipped ? { ...ce.equipped } : null
            next = {
              ...next,
              players: next.players.map((p) => {
                if (p.userId !== ce.userId) return p
                const cur = p.cosmetics ?? { skin: "", glow: "", ring: "", trail: "", sound: "" }
                return { ...p, cosmetics: remoteLoadout !== null ? { ...remoteLoadout } : { ...cur, [slot]: ce.cosmeticId } }
              }),
              // Leaderboard rows carry the same flex cosmetics; keep them in
              // sync so name glow / avatar ring show on the board too.
              ...(next.leaderboard ? {
                leaderboard: next.leaderboard.map((row) => {
                  if (row.userId !== ce.userId) return row
                  const cur = row.cosmetics ?? { skin: "", glow: "", ring: "", trail: "", sound: "" }
                  return { ...row, cosmetics: remoteLoadout !== null ? { ...remoteLoadout } : { ...cur, [slot]: ce.cosmeticId } }
                }),
              } : {}),
            }
          }
          return next
        })
        emitFx(e as unknown as ServerEvent)
        return
      }
      if (e.type === "snapshot") {
        lastSeqRef.current = e.snapshot.seq
        reconnectAttemptsRef.current = 0
        clearReconnectTimer()
        powerRef.current = toNum(e.snapshot.me.clickPower) || 1
        // Snapshot heal: authoritative state clears reflected pendings.
        healPendingFromSnapshot(e.snapshot)
        // Delta merge: lite snapshots (no catalog arrays) merge over cached
        // catalog instead of wiping it.
        setSnap((prev) => {
          const incoming = e.snapshot
          if (prev !== null && (incoming.upgrades === undefined || incoming.modules === undefined || incoming.projects === undefined || incoming.quests === undefined)) {
            return {
              ...incoming,
              players: incoming.players.length > 0 ? incoming.players : prev.players,
              leaderboard: incoming.leaderboard !== undefined && incoming.leaderboard.length > 0 ? incoming.leaderboard : prev.leaderboard,
              upgrades: incoming.upgrades ?? prev.upgrades,
              modules: incoming.modules ?? prev.modules,
              projects: incoming.projects ?? prev.projects,
              quests: incoming.quests ?? prev.quests,
              abilities: incoming.abilities ?? prev.abilities,
              cosmetics: incoming.cosmetics ?? prev.cosmetics,
              guild: incoming.guild ?? prev.guild,
            }
          }
          return incoming
        })
        if (e.snapshot.offlineBonus && e.snapshot.offlineBonus.cookies !== "0") setOfflineBonus(e.snapshot.offlineBonus)
        // Quest claimed/progress is server-authoritative: snapshot replaces
        // local state wholesale, so reconnects can never re-grant.
        return
      }
      if (e.type === "tester_cps") {
        const ev = e as unknown as { userId: string; cps: number; seq: number };
        if (typeof ev.userId === "string" && Number.isFinite(ev.cps)) {
          handleSeq(ev.seq)
          setSnap((s) => {
            if (!s) return s
            const nextLeaderboard = s.leaderboard ? s.leaderboard.map((x) => x.userId === ev.userId ? { ...x, bestTesterCps: Math.max(x.bestTesterCps ?? 0, ev.cps) } : x) : undefined
            const nextPlayers = s.players.map((x) => x.userId === ev.userId ? { ...x, bestTesterCps: Math.max(x.bestTesterCps ?? 0, ev.cps) } : x)
            return { ...s, players: nextPlayers, ...(nextLeaderboard ? { leaderboard: nextLeaderboard } : {}) }
          })
        }
        return
      }
      if (e.type === "click") {
        handleSeq(e.seq)
        if (e.userId === userId) {
          setOptimistic((v) => {
            const q = pendingRef.current
            const head = q.length > 0 ? (q.shift() ?? 0) : toNum(e.amount)
            return Math.max(0, v - head)
          })
          setOptimisticCredits((v) => {
            const q = pendingCreditsRef.current
            const head = q.length > 0 ? (q.shift() ?? 0) : bakerCreditsFor(toNum(e.amount))
            return Math.max(0, v - head)
          })
        }
        setSnap((s) => {
          if (!s) return s
          const nowMs = Date.now()
          let nextPlayers = s.players
          const p = (e as { player?: import("./types").PlayerInfo }).player
          const clickCos = (e as { cosmetics?: import("./types").PlayerCosmetics }).cosmetics
          if (p !== undefined) {
            const exists = s.players.find((x) => x.userId === p.userId)
            if (exists !== undefined) {
              // Clicks must not touch cps/autoLevel: cps is automation-only.
              // Remote players use same live fields (no cached drift).
              // Cosmetics ride along so glow/ring/trail stay fresh on every
              // click even when the throttled presence broadcast is dropped.
              nextPlayers = s.players.map((x) => x.userId === p.userId ? {
                ...x, name: p.name ?? x.name, avatar: p.avatar ?? x.avatar, spec: p.spec ?? x.spec, color: p.color ?? x.color,
                active: true, lastClickAt: nowMs,
                ...(p.cosmetics !== undefined ? { cosmetics: { ...p.cosmetics } } : {}),
                ...(p.clickPower !== undefined ? { clickPower: p.clickPower } : {}),
                ...(p.level !== undefined ? { level: p.level } : {}),
                ...(p.xp !== undefined ? { xp: p.xp } : {}),
                ...(p.totalClicks !== undefined ? { totalClicks: p.totalClicks } : {}),
                ...(p.contribution !== undefined ? { contribution: p.contribution } : {}),
                ...(p.combo !== undefined ? { combo: p.combo } : {}),
              } : x)
            } else {
              nextPlayers = [...s.players, { ...p, cps: p.cps ?? "0", autoLevel: p.autoLevel ?? 0, active: true, lastClickAt: nowMs }]
            }
          } else {
            const exists = s.players.find((x) => x.userId === e.userId)
            // Server click carries live contribution/clickPower for remote players.
            // Cosmetics ride along so glow/ring/trail stay fresh on every
            // click even when the throttled presence broadcast is dropped.
            const livePatch = {
              active: true, lastClickAt: nowMs,
              ...(clickCos !== undefined ? { cosmetics: { ...clickCos } } : {}),
              ...(e.contribution !== undefined ? { contribution: e.contribution } : {}),
              ...(e.clickPower !== undefined ? { clickPower: e.clickPower } : {}),
              ...(e.level !== undefined ? { level: e.level } : {}),
              ...(e.xp !== undefined ? { xp: e.xp } : {}),
              ...(e.totalClicks !== undefined ? { totalClicks: e.totalClicks } : {}),
              ...(e.combo !== undefined ? { combo: e.combo } : {}),
            }
            if (exists !== undefined) {
              nextPlayers = s.players.map((x) => x.userId === e.userId ? { ...x, ...livePatch } : x)
            } else {
              nextPlayers = [...s.players, { userId: e.userId, name: e.userId, spec: "Baker", color: "#38bdf8", active: true, contribution: e.contribution ?? e.amount, lastClickAt: nowMs, autoLevel: 0, cps: e.cps ?? "0", clickPower: e.clickPower, level: e.level, xp: e.xp, totalClicks: e.totalClicks, combo: e.combo, ...(clickCos !== undefined ? { cosmetics: { ...clickCos } } : {}) }]
            }
          }
          const liveBakery = (fallbackCookies: string, fallbackLifetime: string) => ({
            ...s.bakery,
            cookies: e.bakeryCookies ?? fallbackCookies,
            ...(e.bakeryCps !== undefined ? { cps: e.bakeryCps } : {}),
            ...(e.bakeryLevel !== undefined ? { level: e.bakeryLevel } : {}),
            ...(e.bakeryNextLevelCookies !== undefined ? { nextLevelCookies: e.bakeryNextLevelCookies } : {}),
            ...(e.bakeryLevelProgress !== undefined ? { levelProgress: e.bakeryLevelProgress } : {}),
            ...(e.bakeryLifetimeCookies !== undefined ? { lifetimeCookies: e.bakeryLifetimeCookies } : { lifetimeCookies: fallbackLifetime }),
          })
          try {
            const cur = BigInt(s.bakery.cookies)
            const add = BigInt(e.amount)
            const nxt = cur + add
            const best = (nxt > cur ? nxt : cur).toString()
            const lifetimeBase = s.bakery.lifetimeCookies ?? s.bakery.cookies
            let bestLifetime = best
            try {
              const lcur = BigInt(lifetimeBase)
              const lnxt = lcur + add
              bestLifetime = (lnxt > lcur ? lnxt : lcur).toString()
            } catch {
              bestLifetime = best
            }
            if (e.userId === userId) {
              try {
                const serverCred = BigInt(e.credits)
                const nextMe = {
                  ...s.me,
                  credits: serverCred.toString(),
                  ...(e.contribution !== undefined ? { contribution: e.contribution } : {}),
                  ...(e.totalClicks !== undefined ? { totalClicks: e.totalClicks } : {}),
                  ...(e.xp !== undefined ? { xp: e.xp } : {}),
                  ...(e.level !== undefined ? { level: e.level } : {}),
                  ...(e.nextLevelXp !== undefined ? { nextLevelXp: e.nextLevelXp } : {}),
                  ...(e.clickPower !== undefined ? { clickPower: e.clickPower } : {}),
                  ...(e.combo !== undefined ? { combo: e.combo } : {}),
                }
                return {
                  ...s,
                  bakery: liveBakery(best, bestLifetime),
                  me: nextMe,
                  serverTime: nowMs,
                  players: nextPlayers,
                }
              } catch {
                return {
                  ...s,
                  bakery: liveBakery(best, bestLifetime),
                  serverTime: nowMs,
                  players: nextPlayers,
                }
              }
            }
            return {
              ...s,
              bakery: liveBakery(best, bestLifetime),
              serverTime: nowMs,
              players: nextPlayers,
            }
          } catch {
            const cur = toNum(s.bakery.cookies)
            const best = String(cur + toNum(e.amount))
            const lcur = toNum(s.bakery.lifetimeCookies ?? s.bakery.cookies)
            const bestLifetime = String(lcur + toNum(e.amount))
            return {
              ...s,
              bakery: liveBakery(best, bestLifetime),
              serverTime: nowMs,
              players: nextPlayers,
            }
          }
        })
        // Quest progress is server-authoritative and only arrives via
        // snapshot: refresh (throttled 15s, separate from gap resync) while
        // quests are still open so the Claim button enables without a storm.
        if (e.userId === userId) {
          const s = snapRef.current
          if (s?.quests?.some((q) => !q.claimed && q.progress < (q.target ?? 0))) requestQuestSync()
        }
        emitFx(e)
        return
      }
      if (e.type === "player_joined") {
        handleSeq(e.seq)
        setSnap((s) => (s ? { ...s, players: [...s.players.filter((p) => p.userId !== e.player.userId), e.player] } : s))
        emitFx(e)
        return
      }
      if (e.type === "player_left") {
        handleSeq(e.seq)
        setSnap((s) => (s ? { ...s, players: s.players.filter((p) => p.userId !== e.userId) } : s))
        emitFx(e)
        return
      }
      if (e.type === "presence") {
        handleSeq(e.seq)
        setSnap((s) => {
          if (!s) return s
          const nextBakery = {
            ...s.bakery,
            ...(e.bakeryCookies !== undefined ? { cookies: e.bakeryCookies } : {}),
            ...(e.bakeryCps !== undefined ? { cps: e.bakeryCps } : {}),
            ...(e.bakeryLevel !== undefined ? { level: e.bakeryLevel } : {}),
            ...(e.bakeryNextLevelCookies !== undefined ? { nextLevelCookies: e.bakeryNextLevelCookies } : {}),
            ...(e.bakeryLevelProgress !== undefined ? { levelProgress: e.bakeryLevelProgress } : {}),
            ...(e.bakeryLifetimeCookies !== undefined ? { lifetimeCookies: e.bakeryLifetimeCookies } : {}),
          }
          const exists = s.players.find((p) => p.userId === e.player.userId)
          if (exists !== undefined) {
            return { ...s, bakery: nextBakery, players: s.players.map((p) => p.userId === e.player.userId ? { ...p, ...e.player, active: true, lastClickAt: Date.now() } : p) }
          }
          return { ...s, bakery: nextBakery, players: [...s.players, { ...e.player, active: true, lastClickAt: Date.now() }] }
        })
        emitFx(e)
        return
      }
      if (e.type === "event_started") {
        handleSeq(e.seq)
        setSnap((s) => {
          if (!s) return s
          if (e.event.id === "mini_cookie") {
            const cid = e.event.cookieId
            const filtered = cid ? s.events.filter((ev) => !(ev.id === "mini_cookie" && ev.cookieId === cid)) : s.events
            return { ...s, events: [...filtered, e.event] }
          }
          const filtered = s.events.filter((ev) => ev.id !== e.event.id || (ev.sneakyId !== undefined && e.event.sneakyId !== undefined && ev.sneakyId !== e.event.sneakyId))
          return { ...s, events: [...filtered, e.event] }
        })
        emitFx(e)
        return
      }
      if (e.type === "event_ended") {
        handleSeq(e.seq)
        setSnap((s) => {
          if (!s) return s
          if (e.id === "sneaky_cookie") {
            const sid = (e as unknown as { sneakyId?: string }).sneakyId
            if (sid) return { ...s, events: s.events.filter((ev) => !(ev.id === "sneaky_cookie" && ev.sneakyId === sid)) }
            return { ...s, events: s.events.filter((ev) => ev.id !== "sneaky_cookie") }
          }
          if (e.id === "mini_cookie") {
            const cid = (e as unknown as { cookieId?: string }).cookieId
            if (cid) return { ...s, events: s.events.filter((ev) => !(ev.id === "mini_cookie" && ev.cookieId === cid)) }
            return s
          }
          return { ...s, events: s.events.filter((ev) => ev.id !== e.id) }
        })
        emitFx(e)
        return
      }
      if (e.type === "upgrade_purchased") {
        handleSeq(e.seq)
        setSnap((s) => {
          if (!s) return s
          const exists = s.players.find((p) => p.userId === e.userId)
          const livePlayerPatch = {
            autoLevel: e.autoLevel, cps: e.cps, active: true, lastClickAt: Date.now(),
            ...(e.clickPower !== undefined ? { clickPower: e.clickPower } : {}),
          }
          const nextPlayers = exists !== undefined
            ? s.players.map((p) => p.userId === e.userId ? { ...p, ...livePlayerPatch } : p)
            : [...s.players, { userId: e.userId, name: e.userId, spec: "Baker" as const, color: "#38bdf8", active: true, contribution: "0", lastClickAt: Date.now(), autoLevel: e.autoLevel, cps: e.cps, ...(e.clickPower !== undefined ? { clickPower: e.clickPower } : {}) }]
          // Bakery trio stays authoritative together; upgrades never spend
          // bank so only apply when the server actually sends them.
          const eb = e as unknown as { bakeryCookies?: string; bakeryCps?: string; bakeryLifetimeCookies?: string; bakeryLevel?: number; bakeryNextLevelCookies?: string; bakeryLevelProgress?: number }
          const nextBakery = {
            ...s.bakery,
            ...(eb.bakeryCookies !== undefined ? { cookies: eb.bakeryCookies } : {}),
            ...(eb.bakeryCps !== undefined ? { cps: eb.bakeryCps } : {}),
            ...(eb.bakeryLifetimeCookies !== undefined ? { lifetimeCookies: eb.bakeryLifetimeCookies } : {}),
            ...(eb.bakeryLevel !== undefined ? { level: eb.bakeryLevel } : {}),
            ...(eb.bakeryNextLevelCookies !== undefined ? { nextLevelCookies: eb.bakeryNextLevelCookies } : {}),
            ...(eb.bakeryLevelProgress !== undefined ? { levelProgress: eb.bakeryLevelProgress } : {}),
          }
          if (e.userId === userId) {
            const nextUpgrades = { ...(s.me.upgrades ?? {}), [e.upgradeId]: Math.max(s.me.upgrades?.[e.upgradeId] ?? 0, e.level) }
            // Single live source: server clickPower only, no local recompute/max().
            const nextMe = {
              ...s.me,
              upgrades: nextUpgrades,
              ...(e.clickPower !== undefined ? { clickPower: e.clickPower } : {}),
              ...(e.credits !== undefined ? { credits: e.credits } : {}),
            }
            // Keep the catalog row fresh for consecutive buys: level is
            // authoritative, cost is the next-level price for that level.
            const nextCatalog = s.upgrades?.map((u) => {
              if (u.id !== e.upgradeId) return u
              const lvl = Math.max(u.level, e.level, nextUpgrades[e.upgradeId] ?? e.level)
              return { ...u, level: lvl, cost: nextUpgradeCostStr(e.upgradeId, lvl, u.cost) }
            })
            return { ...s, players: nextPlayers, me: nextMe, bakery: nextBakery, ...(nextCatalog ? { upgrades: nextCatalog } : {}) }
          }
          return { ...s, players: nextPlayers, bakery: nextBakery }
        })
        if (e.userId === userId) {
          // Broadcast heal: covers a lost ack (no-op if the ack already
          // resolved the pending).
          confirmPendingFromBroadcast("upgrade", e.upgradeId, e.level)
          const s = snapRef.current
          if (s) {
            try {
              const cur = BigInt(s.me.clickPower ?? "1")
              if (cur > 0n) powerRef.current = Number(cur)
            } catch {
              powerRef.current = toNum(s.me.clickPower) || powerRef.current
            }
          }
        }
        emitFx(e)
        return
      }
      if ((e as { type: string }).type === "module_upgraded") {
        const m = e as unknown as { seq: number; userId: string; moduleId: string; level: number; cps: string; cookies?: string; bakeryCookies?: string; bakeryCps?: string; bakeryLifetimeCookies?: string; bakeryLevel?: number; bakeryNextLevelCookies?: string; bakeryLevelProgress?: number }
        handleSeq(m.seq)
        setSnap((s) => {
          if (!s || !s.modules) return s
          const bank = m.bakeryCookies ?? m.cookies
          const cps = m.bakeryCps ?? m.cps
          return {
            ...s,
            bakery: {
              ...s.bakery,
              ...(cps !== undefined ? { cps } : {}),
              ...(bank !== undefined ? { cookies: bank } : {}),
              ...(m.bakeryLifetimeCookies !== undefined ? { lifetimeCookies: m.bakeryLifetimeCookies } : {}),
              ...(m.bakeryLevel !== undefined ? { level: m.bakeryLevel } : {}),
              ...(m.bakeryNextLevelCookies !== undefined ? { nextLevelCookies: m.bakeryNextLevelCookies } : {}),
              ...(m.bakeryLevelProgress !== undefined ? { levelProgress: m.bakeryLevelProgress } : {}),
            },
            // Fresh catalog for consecutive buys: authoritative level, next
            // price stepped forward for any level gap.
            modules: s.modules.map((x) => {
              if (x.id !== m.moduleId) return x
              const lvl = Math.max(x.level, m.level)
              const cost = lvl > x.level ? escalateCostStr(x.cost, lvl - x.level) : x.cost
              return { ...x, level: lvl, cost }
            }),
          }
        })
        if (m.userId === userId) confirmPendingFromBroadcast("module", m.moduleId, m.level)
        emitFx(e)
        return
      }
      if ((e as { type: string }).type === "project_completed") {
        const m = e as unknown as { seq: number; projectId: string; cpsBonus: string; cookies?: string; cps?: string; bakeryCookies?: string; bakeryCps?: string; bakeryLifetimeCookies?: string; bakeryLevel?: number; bakeryNextLevelCookies?: string; bakeryLevelProgress?: number }
        handleSeq(m.seq)
        setSnap((s) => {
          if (!s || !s.projects) return s
          const bank = m.bakeryCookies ?? m.cookies
          const cps = m.bakeryCps ?? m.cps
          return {
            ...s,
            bakery: {
              ...s.bakery,
              ...(bank !== undefined ? { cookies: bank } : {}),
              ...(cps !== undefined ? { cps } : {}),
              ...(m.bakeryLifetimeCookies !== undefined ? { lifetimeCookies: m.bakeryLifetimeCookies } : {}),
              ...(m.bakeryLevel !== undefined ? { level: m.bakeryLevel } : {}),
              ...(m.bakeryNextLevelCookies !== undefined ? { nextLevelCookies: m.bakeryNextLevelCookies } : {}),
              ...(m.bakeryLevelProgress !== undefined ? { levelProgress: m.bakeryLevelProgress } : {}),
            },
            projects: s.projects.map((x) => x.id === m.projectId ? { ...x, status: "completed", progress: x.cookieCost, progressPct: 100, timeLeftMs: 0 } : x),
          }
        })
        requestResync()
        emitFx(e)
        return
      }
      if ((e as { type: string }).type === "project_started") {
        const m = e as unknown as { seq: number; projectId: string; startedAt: number; endsAt: number; durationMs: number; cookies?: string; cps?: string; bakeryCookies?: string; bakeryCps?: string; bakeryLifetimeCookies?: string; bakeryLevel?: number; bakeryNextLevelCookies?: string; bakeryLevelProgress?: number; cost?: string; personalCost?: string; credits?: string }
        handleSeq(m.seq)
        setSnap((s) => {
          if (!s || !s.projects) return s
          const bank = m.bakeryCookies ?? m.cookies
          const cps = m.bakeryCps ?? m.cps
          return {
            ...s,
            bakery: {
              ...s.bakery,
              ...(bank !== undefined ? { cookies: bank } : {}),
              ...(cps !== undefined ? { cps } : {}),
              ...(m.bakeryLifetimeCookies !== undefined ? { lifetimeCookies: m.bakeryLifetimeCookies } : {}),
              ...(m.bakeryLevel !== undefined ? { level: m.bakeryLevel } : {}),
              ...(m.bakeryNextLevelCookies !== undefined ? { nextLevelCookies: m.bakeryNextLevelCookies } : {}),
              ...(m.bakeryLevelProgress !== undefined ? { levelProgress: m.bakeryLevelProgress } : {}),
            },
            ...(m.credits !== undefined ? { me: { ...s.me, credits: m.credits } } : {}),
            projects: s.projects.map((x) => x.id === m.projectId ? { ...x, status: "in_progress", progress: "0", startedAt: m.startedAt, endsAt: m.endsAt, durationMs: m.durationMs, progressPct: 0, timeLeftMs: Math.max(0, m.endsAt - Date.now()) } : x),
          }
        })
        confirmPendingFromBroadcast("project", m.projectId, undefined, m.cost, m.personalCost)
        requestResync()
        emitFx(e)
        return
      }
      if ((e as { type: string }).type === "spec_chosen") {
        const m = e as unknown as { seq: number; userId: string; specId: string }
        handleSeq(m.seq)
        if (m.userId === userId) {
          setSnap((s) => {
            if (!s) return s
            return { ...s, me: { ...s.me, specialization: m.specId } }
          })
          confirmPendingFromBroadcast("spec", m.specId)
          requestResync()
        }
        emitFx(e)
        return
      }
      if ((e as { type: string }).type === "ability_activated") {
        const m = e as unknown as { seq: number; userId: string; abilityId: string; cost?: string; credits?: string; cooldownMs?: number; cooldownRemainingMs?: number; endsAt?: number; durationMs?: number; amount?: string; creditsGained?: string; bakeryCookies?: string; bakeryCps?: string; bakeryLifetimeCookies?: string; bakeryLevel?: number; bakeryNextLevelCookies?: string; bakeryLevelProgress?: number }
        handleSeq(m.seq)
        const label = displayNameFor("ability", m.abilityId)
        if (m.userId === userId) {
          // Own activation: merge authoritative credits + bakery trio +
          // cooldown/active timers, then confirm any still-open pending
          // (covers a lost ack; no-op if the ack already resolved it).
          setSnap((s) => {
            if (!s) return s
            let next = s
            if (m.credits !== undefined) next = { ...next, me: { ...next.me, credits: m.credits } }
            const hasTrio = m.bakeryCookies !== undefined || m.bakeryCps !== undefined || m.bakeryLifetimeCookies !== undefined || m.bakeryLevel !== undefined
            if (hasTrio) {
              next = {
                ...next,
                bakery: {
                  ...next.bakery,
                  ...(m.bakeryCookies !== undefined ? { cookies: m.bakeryCookies } : {}),
                  ...(m.bakeryCps !== undefined ? { cps: m.bakeryCps } : {}),
                  ...(m.bakeryLifetimeCookies !== undefined ? { lifetimeCookies: m.bakeryLifetimeCookies } : {}),
                  ...(m.bakeryLevel !== undefined ? { level: m.bakeryLevel } : {}),
                  ...(m.bakeryNextLevelCookies !== undefined ? { nextLevelCookies: m.bakeryNextLevelCookies } : {}),
                  ...(m.bakeryLevelProgress !== undefined ? { levelProgress: m.bakeryLevelProgress } : {}),
                },
              }
            }
            if (next.abilities) {
              const nowMs = Date.now()
              next = {
                ...next,
                abilities: next.abilities.map((x) => {
                  if (x.id !== m.abilityId) return x
                  const cd = typeof m.cooldownMs === "number" ? m.cooldownMs : x.cooldownMs
                  const activeMs = typeof m.endsAt === "number" ? Math.max(0, m.endsAt - nowMs) : x.activeRemainingMs
                  return { ...x, cooldownMs: cd, cooldownRemainingMs: typeof m.cooldownRemainingMs === "number" ? m.cooldownRemainingMs : cd, activeRemainingMs: activeMs, endsAt: typeof m.endsAt === "number" ? m.endsAt : x.endsAt }
                }),
              }
            }
            return next
          })
          confirmPendingFromBroadcast("ability", m.abilityId)
          // No SFX here: the ack (or the confirm above, if the broadcast won
          // the race) already played it. Unconditional sound would double-play
          // in the normal ack-then-broadcast flow.
        } else {
          // Teammate activation: visible broadcast effect for everyone.
          // Name resolves from the current snapshot ref (never inside the
          // setSnap updater, which must stay side-effect free).
          const who = snapRef.current?.players.find((x) => x.userId === m.userId)?.name ?? "A baker"
          pushNotice(`✨ ${who} used ${label}.`, "ability")
          try {
            sfxAbility()
          } catch {
            void 0
          }
        }
        emitFx(e as unknown as ServerEvent)
        return
      }
      if (e.type === "sneaky_collected") {
        handleSeq(e.seq)
        setSneakyFlash({ amount: e.amount, credits: e.credits, x: e.x, y: e.y, seq: e.seq })
        window.setTimeout(() => setSneakyFlash(null), 2400)
        setSnap((s) => {
          if (!s) return s
          let next = s
          try {
            const cur = BigInt(s.bakery.cookies)
            const add = BigInt(e.amount)
            const lcur = BigInt(s.bakery.lifetimeCookies ?? s.bakery.cookies)
            next = { ...next, bakery: { ...next.bakery, cookies: (cur + add).toString(), lifetimeCookies: (lcur + add).toString() } }
          } catch {
            next = { ...next, bakery: { ...next.bakery, cookies: String(toNum(s.bakery.cookies) + toNum(e.amount)), lifetimeCookies: String(toNum(s.bakery.lifetimeCookies ?? s.bakery.cookies) + toNum(e.amount)) } }
          }
          if (e.userId === userId) {
            try {
              const curCred = BigInt(s.me.credits)
              const addCred = BigInt(e.credits)
              next = { ...next, me: { ...next.me, credits: (curCred + addCred).toString() } }
            } catch {
              next = { ...next, me: { ...next.me, credits: String(toNum(s.me.credits) + toNum(e.credits)) } }
            }
            return { ...next, events: next.events.filter((ev) => !(ev.id === "sneaky_cookie" && ev.sneakyId === e.sneakyId)) }
          }
          return { ...next, events: next.events.filter((ev) => !(ev.id === "sneaky_cookie" && ev.sneakyId === e.sneakyId)) }
        })
        if (e.userId === userId) requestResync()
        emitFx(e)
        return
      }
      if (e.type === "mini_collected") {
        handleSeq(e.seq)
        setMiniFlash({ amount: e.amount, credits: e.credits, multiplier: e.multiplier, x: e.x, y: e.y, seq: e.seq })
        window.setTimeout(() => setMiniFlash(null), 2400)
        setSnap((s) => {
          if (!s) return s
          let next = s
          try {
            const cur = BigInt(s.bakery.cookies)
            const add = BigInt(e.amount)
            const lcur = BigInt(s.bakery.lifetimeCookies ?? s.bakery.cookies)
            next = { ...next, bakery: { ...next.bakery, cookies: (cur + add).toString(), lifetimeCookies: (lcur + add).toString() } }
          } catch {
            next = { ...next, bakery: { ...next.bakery, cookies: String(toNum(s.bakery.cookies) + toNum(e.amount)), lifetimeCookies: String(toNum(s.bakery.lifetimeCookies ?? s.bakery.cookies) + toNum(e.amount)) } }
          }
          if (e.userId === userId) {
            try {
              const curCred = BigInt(s.me.credits)
              const addCred = BigInt(e.credits)
              next = { ...next, me: { ...next.me, credits: (curCred + addCred).toString() } }
            } catch {
              next = { ...next, me: { ...next.me, credits: String(toNum(s.me.credits) + toNum(e.credits)) } }
            }
            return { ...next, events: next.events.filter((ev) => !(ev.id === "mini_cookie" && ev.cookieId === e.cookieId)) }
          }
          return { ...next, events: next.events.filter((ev) => !(ev.id === "mini_cookie" && ev.cookieId === e.cookieId)) }
        })
        if (e.userId === userId) requestResync()
        emitFx(e)
        return
      }
      if ((e as { type: string }).type === "golden_redeemed") {
        const g = e as unknown as { seq: number; userId: string; amount: string; credits: string; x: number; y: number; frenzyEndsAt: number }
        handleSeq(g.seq)
        setGoldenFlash({ amount: g.amount, credits: g.credits, x: g.x, y: g.y, frenzyEndsAt: g.frenzyEndsAt, seq: g.seq })
        window.setTimeout(() => setGoldenFlash((cur) => (cur !== null && cur.seq === g.seq ? null : cur)), 6000)
        setSnap((s) => {
          if (!s) return s
          let next = s
          try {
            const cur = BigInt(s.bakery.cookies)
            const add = BigInt(g.amount)
            const lcur = BigInt(s.bakery.lifetimeCookies ?? s.bakery.cookies)
            next = { ...next, bakery: { ...next.bakery, cookies: (cur + add).toString(), lifetimeCookies: (lcur + add).toString() } }
          } catch {
            next = { ...next, bakery: { ...next.bakery, cookies: String(toNum(s.bakery.cookies) + toNum(g.amount)), lifetimeCookies: String(toNum(s.bakery.lifetimeCookies ?? s.bakery.cookies) + toNum(g.amount)) } }
          }
          if (g.userId === userId) {
            try {
              const curCred = BigInt(s.me.credits)
              const addCred = BigInt(g.credits)
              next = { ...next, me: { ...next.me, credits: (curCred + addCred).toString() } }
            } catch {
              next = { ...next, me: { ...next.me, credits: String(toNum(s.me.credits) + toNum(g.credits)) } }
            }
          }
          // The global frenzy window stays live: keep golden_cookie in
          // events so the banner + timer keep counting down.
          return next
        })
        // goldenCaught quest progress is server-authoritative via snapshot.
        if (g.userId === userId) requestQuestSync()
        emitFx(e)
        return
      }
    })
    const connectTask = dedupedSharedConnect(sharedKey, conn)
    connectTask.catch(() => {
      if (!mountedRef.current) return
      if (snapRef.current !== null) {
        setError("Could not reach the bakery. Retrying…")
        scheduleReconnect()
        return
      }
      window.setTimeout(() => {
        if (!mountedRef.current) return
        if (snapRef.current !== null) return
        setError("Could not reach the bakery. Retrying…")
        scheduleReconnect()
      }, API_GRACE_MS)
    })
    return () => {
      mountedRef.current = false
      clearReconnectTimer()
      try {
        off?.()
      } catch {
        return
      }
      if (connRef.current === conn) connRef.current = null
      for (const t of pendingTimersRef.current) window.clearTimeout(t)
      pendingTimersRef.current = []
      pendingRef.current = []
      pendingCreditsRef.current = []
      releaseSharedConnection(sharedKey)
    }
  }, [ready, cleanGuildId, token, userId, attempt, emitFx, handleSeq, requestResync, scheduleReconnect, clearReconnectTimer, replayPendingBuys, confirmPendingFromBroadcast, healPendingFromSnapshot])

  useEffect(() => {
    if (!ready) return
    if (initialFetchGuildRef.current === cleanGuildId) return
    initialFetchGuildRef.current = cleanGuildId
    let cancelled = false
    void apiFetch<Snapshot>("/api/bakery/" + encodeURIComponent(cleanGuildId), { token, timeoutMs: API_TIMEOUT_MS })
      .then((s) => {
        if (cancelled) return
        lastSeqRef.current = s.seq
        powerRef.current = toNum(s.me.clickPower) || 1
        setSnap(s)
        if (s.offlineBonus && s.offlineBonus.cookies !== "0") setOfflineBonus(s.offlineBonus)
        reconnectAttemptsRef.current = 0
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [ready, cleanGuildId, token])

  useEffect(() => {
    creatingRetryDone.current = false
  }, [cleanGuildId])

  useEffect(() => {
    if (error !== "Creating bakery...") return
    if (creatingRetryDone.current) return
    if (!ready) return
    creatingRetryDone.current = true
    let cancelled = false
    void (async () => {
      try {
        await apiFetch("/api/bakery/" + encodeURIComponent(cleanGuildId), { token, timeoutMs: API_TIMEOUT_MS })
        if (cancelled) return
        setError(null)
        setAttempt((a) => a + 1)
      } catch (e) {
        if (cancelled) return
        const is404 = typeof e === "object" && e !== null && "status" in e && (e as { status: unknown }).status === 404
        if (is404) {
          setError("Bakery not found — pick another server.")
          return
        }
        const msg = e instanceof Error ? e.message.toLowerCase() : ""
        if (msg.includes("bakery_not_found") || msg.includes("unknown_bakery")) {
          setError("Bakery not found — pick another server.")
          return
        }
        if (cancelled) return
        setError(null)
        setAttempt((a) => a + 1)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [error, ready, cleanGuildId, token])

  const refresh = useCallback(async () => {
    if (!ready) return
    try {
      const s = await apiFetch<Snapshot>("/api/bakery/" + encodeURIComponent(cleanGuildId), { token, timeoutMs: API_TIMEOUT_MS })
      lastSeqRef.current = s.seq
      powerRef.current = toNum(s.me.clickPower) || 1
      setSnap(s)
      if (s.offlineBonus && s.offlineBonus.cookies !== "0") setOfflineBonus(s.offlineBonus)
    } catch {
      return
    }
  }, [ready, cleanGuildId, token])

  // Consecutive buys stack: no item-level in-flight block. Each purchase
  // gets a fresh requestId and prices the next level (settled level +
  // in-flight count) from the latest snapshot/ack. Optimistic apply bumps
  // both me.upgrades and the catalog row so the UI cost/level is fresh
  // instantly; the ack reconciles authoritative BigInt strings.
  const buyUpgrade = useCallback((upgradeId: string) => {
    const s = snapRef.current
    if (!s) {
      return
    }
    const row = s.upgrades?.find((u) => u.id === upgradeId)
    if (!row) {
      pushNotice("Unknown item.", "upgrade")
      return
    }
    const stacked = pendingCountFor("upgrade", upgradeId)
    const settledLevel = s.me.upgrades?.[upgradeId] ?? row.level ?? 0
    const level = settledLevel
    const maxLevel = row.maxLevel ?? upgradeDefFor(upgradeId)?.maxLevel ?? 99
    if (level >= maxLevel) {
      pushNotice("Already maxed.", "upgrade")
      return
    }
    const spec = (s.me.specialization ?? "baker").trim().toLowerCase()
    const bakeryLevel = s.bakery.level ?? 1
    const specNeed = (row.specLocked ?? "").trim().toLowerCase()
    // Buyer-spec gate (not lobby presence): the buyer must BE that spec.
    // Having the spec elsewhere in the lobby never unlocks upgrades.
    if (specNeed !== "" && spec !== specNeed) {
      pushNotice(`Locked — requires ${specNeed} spec (you must be ${specNeed}).`, "upgrade")
      return
    }
    if (row.locked === true) {
      // No overwarn: bare server lock never names a spec (see UpgradesPage).
      pushNotice(specNeed !== "" && spec !== specNeed ? `Locked — requires ${specNeed} spec (you must be ${specNeed}).` : "Locked — level up first.", "upgrade")
      return
    }
    const unlockLevel = row.unlockBakeryLevel ?? 1
    if (bakeryLevel < unlockLevel) {
      pushNotice("Locked — level up first.", "upgrade")
      return
    }
    const reqId = (row.requiresId ?? "").trim()
    const reqLv = row.requiresLevel ?? 0
    if (reqId !== "") {
      let have = 0
      const live = s.me.upgrades ?? {}
      const direct = live[reqId]
      if (typeof direct === "number" && Number.isFinite(direct)) have = Math.floor(direct)
      else {
        const want = reqId.trim().toLowerCase()
        for (const [k, v] of Object.entries(live)) {
          if (k.trim().toLowerCase() === want && typeof v === "number" && Number.isFinite(v)) {
            have = Math.floor(v)
            break
          }
        }
      }
      if (have < reqLv) {
        pushNotice("Locked — upgrade the requirement first.", "upgrade")
        return
      }
    }
    // Fresh level+cost: the catalog cost is for the settled level; each
    // stacked in-flight buy escalates one 1.5x step from it.
    const costStr = stacked > 0 ? escalateCostStr(row.cost, stacked) : row.cost
    let need: bigint
    try {
      need = BigInt(costStr)
    } catch {
      return
    }
    // Live-credits validation: snapshot credits go stale between server
    // settles; the displayed liveCredits adds optimistic clicks + passive
    // CPS accrual. Reject only when the live total still falls short, and
    // always send borderline buys — the server is authoritative.
    try {
      const needNum = toNum(costStr)
      const liveHave = Math.max(toNum(s.me.credits), Math.floor(liveCreditsRef.current))
      if (liveHave < needNum && BigInt(s.me.credits) < need) {
        pushNotice(`Not enough Baker Credits (need ${fmtCompact(needNum)}, have ${fmtCompact(liveHave)}).`, "upgrade")
        return
      }
    } catch {
      return
    }
    const costNum = toNum(costStr)
    const requestId = newRequestId()
    const payload = { type: "buy_upgrade", id: upgradeId, requestId }
    pendingBuysRef.current.set(requestId, { kind: "upgrade", id: upgradeId, itemKey: "upgrade:" + upgradeId.toLowerCase(), cost: costNum, payload, prevCredits: s.me.credits, prevCookies: s.bakery.cookies, prevPower: s.me.clickPower, prevUpgrades: { ...(s.me.upgrades ?? {}) }, prevModules: s.modules ? s.modules.map((x) => ({ ...x })) as Snapshot["modules"] : null, prevProjects: s.projects ? s.projects.map((x) => ({ ...x })) as Snapshot["projects"] : null, prevSpec: s.me.specialization ?? "baker" })
    syncPendingKey("upgrade", upgradeId)
    const chargeStr = costStr
    const expectLevel = level + 1
    setSnap((prev) => {
      if (!prev) return prev
      try {
        const cur = BigInt(prev.me.credits)
        const next = cur - BigInt(chargeStr)
        const nextUpgrades = { ...(prev.me.upgrades ?? {}), [upgradeId]: (prev.me.upgrades?.[upgradeId] ?? level) + 1 }
        let out = { ...prev, me: { ...prev.me, credits: next.toString(), upgrades: nextUpgrades } }
        if (out.upgrades) {
          out = {
            ...out,
            upgrades: out.upgrades.map((u) => {
              if (u.id !== upgradeId) return u
              const lv = Math.max(u.level + 1, expectLevel)
              return { ...u, level: lv, cost: nextUpgradeCostStr(upgradeId, lv, chargeStr) }
            }),
          }
        }
        return out
      } catch {
        return prev
      }
    })
    // Pure optimistic apply above is instantly visible. Fire-and-forget:
    // one WS msg with a fresh requestId (server dedupes only retries of the
    // same requestId, never distinct purchases). The socket queues while
    // offline and pendings replay on reconnect; only an explicit server
    // nack rolls back. No timeout path.
    fireBuy({ type: "buy_upgrade", id: upgradeId, requestId })
  }, [pushNotice, fireBuy, pendingCountFor, syncPendingKey])

  // Same stacking contract as buyUpgrade: fresh requestId per purchase,
  // fresh level+cost per buy, optimistic catalog bump, ack reconciles.
  const buyModule = useCallback((moduleId: string) => {    const s = snapRef.current
    if (!s || !s.modules) {
      return
    }
    const row = s.modules.find((u) => u.id === moduleId)
    if (!row) {
      pushNotice("Unknown item.", "module")
      return
    }
    const stacked = pendingCountFor("module", moduleId)
    const settledLevel = row.level ?? 0
    if (row.maxLevel !== undefined && settledLevel + stacked >= row.maxLevel) {
      pushNotice("Already maxed.", "module")
      return
    }
    if (row.locked) {
      pushNotice("Locked — level up first.", "module")
      return
    }
    const costStr = stacked > 0 ? escalateCostStr(row.cost, stacked) : row.cost
    let need: bigint
    try {
      need = BigInt(costStr)
    } catch {
      return
    }
    // Live-bank validation: snapshot bank goes stale between server settles
    // (tick is 2s) and ignores unacked clicks. Validate against the same
    // live total the UI shows (bank + elapsed*CPS + optimistic) and let the
    // server (settle-then-check) decide borderline cases.
    try {
      const have = liveBankFor(s)
      if (have < need) {
        pushNotice(`Not enough bakery cookies (need ${fmtCompact(costStr)}, have ${fmtCompact(have.toString())}).`, "module")
        return
      }
    } catch {
      return
    }
    const cost = toNum(costStr)
    const requestId = newRequestId()
    const payload = { type: "buy_module", id: moduleId, requestId }
    pendingBuysRef.current.set(requestId, { kind: "module", id: moduleId, itemKey: "module:" + moduleId.toLowerCase(), cost, payload, prevCredits: s.me.credits, prevCookies: s.bakery.cookies, prevPower: s.me.clickPower, prevUpgrades: { ...(s.me.upgrades ?? {}) }, prevModules: s.modules.map((x) => ({ ...x })) as Snapshot["modules"], prevProjects: s.projects ? s.projects.map((x) => ({ ...x })) as Snapshot["projects"] : null, prevSpec: s.me.specialization ?? "baker" })
    syncPendingKey("module", moduleId)
    const chargeStr = costStr
    setSnap((prev) => {
      if (!prev || !prev.modules) return prev
      try {
        const cur = BigInt(prev.bakery.cookies)
        const next = cur - BigInt(chargeStr)
        return {
          ...prev,
          bakery: { ...prev.bakery, cookies: next.toString() },
          modules: prev.modules.map((x) => x.id === moduleId ? { ...x, level: x.level + 1, cost: escalateCostStr(x.cost, 1) } : x),
        }
      } catch {
        return prev
      }
    })
    // Pure optimistic apply above is instantly visible; fire-and-forget send
    // (queued offline, replayed on reconnect). Only a server nack rolls back.
    fireBuy({ type: "buy_module", id: moduleId, requestId })
  }, [pushNotice, fireBuy, pendingCountFor, liveBankFor, syncPendingKey])

  const buyProject = useCallback((projectId: string) => {
    const s = snapRef.current
    if (!s) {
      return
    }
    const row = s.projects?.find((u) => u.id === projectId)
    if (!row) {
      pushNotice("Unknown item.", "project")
      return
    }
    if (row.status === "completed") {
      pushNotice("Project already completed.", "project")
      return
    }
    const needLevel = row.requiredBakeryLevel ?? 1
    if (s.bakery.level < needLevel) {
      pushNotice("Locked — level up first.", "project")
      return
    }
    const done = new Set((s.projects ?? []).filter((p) => p.status === "completed").map((p) => p.id))
    if (row.requiresProjectId && !done.has(row.requiresProjectId)) {
      pushNotice("Locked — upgrade the requirement first.", "project")
      return
    }
    if (row.requiresAny && row.requiresAny.length > 0 && !row.requiresAny.some((id) => done.has(id))) {
      pushNotice("Locked — upgrade the requirement first.", "project")
      return
    }
    if (row.exclusiveGroup) {
      const rival = (s.projects ?? []).find((p) => p.id !== projectId && p.exclusiveGroup === row.exclusiveGroup && p.status === "completed")
      if (rival) {
        pushNotice("Locked — rival branch already built.", "project")
        return
      }
    }
    const requiredSpecs = row.requiredSpecs ?? []
    const missingSpecs = row.missingSpecs ?? requiredSpecs.filter((sp) => !(row.presentSpecs ?? []).includes(sp))
    if (missingSpecs.length > 0) {
      pushNotice(`Locked — needs active ${missingSpecs.join(" + ")}.`, "project")
      return
    }
    if ((row.cooldownRemainingMs ?? 0) > 0) {
      pushNotice("Construction crew busy — wait for cooldown.", "project")
      return
    }
    let need: bigint
    try {
      need = BigInt(row.cookieCost)
    } catch {
      return
    }
    const feeStr = projectPersonalFeeStr(row.cookieCost, row.personalCost)
    let fee: bigint
    try {
      fee = BigInt(feeStr)
    } catch {
      return
    }
    // Same live-bank validation as buyModule: never reject on the stale
    // snapshot bank when CPS/optimistic clicks already cover the cost.
    // Backend is authoritative: both bakery stock and the buyer's personal
    // Baker-Credit stake (10%) are checked again server-side.
    try {
      const have = liveBankFor(s)
      if (have < need) {
        pushNotice(`Not enough bakery cookies (need ${fmtCompact(row.cookieCost)}, have ${fmtCompact(have.toString())}).`, "project")
        return
      }
    } catch {
      return
    }
    try {
      const liveHave = Math.max(toNum(s.me.credits), Math.floor(liveCreditsRef.current))
      const feeNum = toNum(feeStr)
      if (BigInt(s.me.credits) < fee && liveHave < feeNum) {
        pushNotice(`Not enough personal credits (need 🪙 ${fmtCompact(feeStr)}, have ${fmtCompact(String(liveHave))}).`, "project")
        return
      }
    } catch {
      return
    }
    const cost = toNum(row.cookieCost)
    if (inflightBuyItem("project", projectId)) {
      pushNotice("Already building…", "project")
      return
    }
    const requestId = newRequestId()
    const payload = { type: "build_project", id: projectId, requestId }
    pendingBuysRef.current.set(requestId, { kind: "project", id: projectId, itemKey: "project:" + projectId.toLowerCase(), cost, payload, prevCredits: s.me.credits, prevCookies: s.bakery.cookies, prevPower: s.me.clickPower, prevUpgrades: { ...(s.me.upgrades ?? {}) }, prevModules: s.modules ? s.modules.map((x) => ({ ...x })) as Snapshot["modules"] : null, prevProjects: s.projects ? s.projects.map((x) => ({ ...x })) as Snapshot["projects"] : null, prevSpec: s.me.specialization ?? "baker" })
    syncPendingKey("project", projectId)
    const nowMs = Date.now()
    const durMs = row.durationMs ?? 30 * 60_000
    setSnap((prev) => {
      if (!prev || !prev.projects) return prev
      try {
        const cur = BigInt(prev.bakery.cookies)
        const next = cur - need
        const cred = BigInt(prev.me.credits) - fee
        return { ...prev, bakery: { ...prev.bakery, cookies: next.toString() }, me: { ...prev.me, credits: cred.toString() }, projects: prev.projects.map((x) => x.id === projectId ? { ...x, status: "in_progress", progress: "0", startedAt: nowMs, endsAt: nowMs + durMs, progressPct: 0, timeLeftMs: durMs } : x) }
      } catch {
        return prev
      }
    })
    // Pure optimistic apply above is instantly visible; fire-and-forget send
    // (queued offline, replayed on reconnect). Only a server nack rolls back.
    fireBuy({ type: "build_project", id: projectId, requestId })
  }, [pushNotice, fireBuy, inflightBuyItem, liveBankFor, syncPendingKey])

  // Cosmetic store: one-shot buys (no levels) + slot equips. Backend is
  // authoritative for ownership, funds, and loadout; the client applies the
  // same instant-optimistic pattern as other buys (only an explicit server
  // nack rolls back). Skins change the BakeScene cookie, glow/ring/trail are
  // subtle user-visible flex with zero gameplay effect.
  const buyCosmetic = useCallback((cosmeticId: string) => {
    const s = snapRef.current
    if (!s) return
    const id = cosmeticId.trim().toLowerCase()
    if (id === "") return
    if (inflightBuyItem("cosmetic", id)) return
    const row = s.cosmetics?.find((x) => x.id === id)
    const def = COSMETICS.find((c) => c.id === id)
    const costStr = row?.cost ?? (def !== undefined ? String(def.cost) : undefined)
    const currency = row?.currency ?? def?.currency ?? "credits"
    if (costStr === undefined) {
      pushNotice("Unknown cosmetic.", "cosmetic")
      return
    }
    const owned = (s.me.cosmetics?.owned ?? []).includes(id) || row?.owned === true
    if (owned) {
      pushNotice("Already owned.", "cosmetic")
      return
    }
    let need: bigint
    try {
      need = BigInt(costStr)
    } catch {
      return
    }
    if (currency === "cookies") {
      try {
        const have = liveBankFor(s)
        if (have < need) {
          pushNotice(`Not enough bakery cookies (need 🍪 ${fmtCompact(costStr)}, have ${fmtCompact(have.toString())}).`, "cosmetic")
          return
        }
      } catch {
        return
      }
    } else {
      try {
        const liveHave = Math.max(toNum(s.me.credits), Math.floor(liveCreditsRef.current))
        if (BigInt(s.me.credits) < need && liveHave < toNum(costStr)) {
          pushNotice(`Not enough Baker Credits (need 🪙 ${fmtCompact(costStr)}, have ${fmtCompact(String(liveHave))}).`, "cosmetic")
          return
        }
      } catch {
        return
      }
    }
    const costNum = toNum(costStr)
    const requestId = newRequestId()
    const payload = { type: "buy_cosmetic", id, requestId }
    pendingBuysRef.current.set(requestId, { kind: "cosmetic", id, itemKey: "cosmetic:" + id, cost: costNum, payload, prevCredits: s.me.credits, prevCookies: s.bakery.cookies, prevPower: s.me.clickPower, prevUpgrades: { ...(s.me.upgrades ?? {}) }, prevModules: s.modules ? s.modules.map((x) => ({ ...x })) as Snapshot["modules"] : null, prevProjects: s.projects ? s.projects.map((x) => ({ ...x })) as Snapshot["projects"] : null, prevSpec: s.me.specialization ?? "baker", prevCosmetics: s.cosmetics ? s.cosmetics.map((x) => ({ ...x })) : null, prevEquipped: s.me.cosmetics ? { ...s.me.cosmetics.equipped } : null })
    syncPendingKey("cosmetic", id)
    setSnap((prev) => {
      if (!prev) return prev
      try {
        let next = prev
        if (currency === "cookies") {
          const cur = BigInt(prev.bakery.cookies)
          next = { ...next, bakery: { ...next.bakery, cookies: (cur - BigInt(costStr)).toString() } }
        } else {
          const cur = BigInt(prev.me.credits)
          next = { ...next, me: { ...next.me, credits: (cur - BigInt(costStr)).toString() } }
        }
        if (next.me.cosmetics && !next.me.cosmetics.owned.includes(id)) {
          next = { ...next, me: { ...next.me, cosmetics: { owned: [...next.me.cosmetics.owned, id], equipped: { ...next.me.cosmetics.equipped } } } }
        }
        if (next.cosmetics) {
          next = { ...next, cosmetics: next.cosmetics.map((x) => x.id === id ? { ...x, owned: true } : x) }
        }
        return next
      } catch {
        return prev
      }
    })
    // Pure optimistic apply above is instantly visible; fire-and-forget send
    // (queued offline, replayed on reconnect). Only a server nack rolls back.
    fireBuy({ type: "buy_cosmetic", id, requestId })
  }, [pushNotice, fireBuy, inflightBuyItem, liveBankFor, syncPendingKey])

  const equipCosmetic = useCallback((cosmeticId: string, slot?: string) => {
    const s = snapRef.current
    if (!s) return
    const raw = cosmeticId.trim().toLowerCase()
    const slotParam = (slot ?? "").trim().toLowerCase()
    const clearing = raw === "" || raw === "none" || raw.startsWith("none:") || raw === "clear"
    const clearSlot = clearing ? (raw.startsWith("none:") ? raw.slice("none:".length) : slotParam) : ""
    if (clearing && clearSlot !== "skin" && clearSlot !== "glow" && clearSlot !== "ring" && clearSlot !== "trail" && clearSlot !== "sound") {
      pushNotice("Unknown slot.", "equip")
      return
    }
    const target = clearing ? "none:" + clearSlot : raw
    if (inflightBuyItem("equip", target)) return
    if (!clearing) {
      const row = s.cosmetics?.find((x) => x.id === raw)
      const kind = row?.kind ?? COSMETICS.find((c) => c.id === raw)?.kind ?? slotParam
      if (kind === "") {
        pushNotice("Unknown cosmetic.", "equip")
        return
      }
      if (slotParam !== "" && slotParam !== kind) {
        pushNotice("Unknown slot.", "equip")
        return
      }
      const owned = (s.me.cosmetics?.owned ?? []).includes(raw) || row?.owned === true
      if (!owned) {
        pushNotice("You don't own that yet.", "equip")
        return
      }
      const eq = s.me.cosmetics?.equipped
      if (eq && (eq as unknown as Record<string, string>)[kind] === raw) {
        pushNotice("Already equipped.", "equip")
        return
      }
    }
    const effSlot = clearing ? clearSlot : (s.cosmetics?.find((x) => x.id === raw)?.kind ?? COSMETICS.find((c) => c.id === raw)?.kind ?? slotParam)
    const requestId = newRequestId()
    const payload = clearing ? { type: "equip_cosmetic", id: "none", slot: clearSlot, requestId } : { type: "equip_cosmetic", id: raw, slot: effSlot, requestId }
    pendingBuysRef.current.set(requestId, { kind: "equip", id: target, itemKey: "equip:" + target, cost: 0, payload, prevCredits: s.me.credits, prevCookies: s.bakery.cookies, prevPower: s.me.clickPower, prevUpgrades: { ...(s.me.upgrades ?? {}) }, prevModules: s.modules ? s.modules.map((x) => ({ ...x })) as Snapshot["modules"] : null, prevProjects: s.projects ? s.projects.map((x) => ({ ...x })) as Snapshot["projects"] : null, prevSpec: s.me.specialization ?? "baker", prevCosmetics: s.cosmetics ? s.cosmetics.map((x) => ({ ...x })) : null, prevEquipped: s.me.cosmetics ? { ...s.me.cosmetics.equipped } : null })
    syncPendingKey("equip", target)
    const applySlot = effSlot
    const applyId = clearing ? "" : raw
    setSnap((prev) => {
      if (!prev) return prev
      let next = prev
      if (next.me.cosmetics && applySlot !== "") {
        next = { ...next, me: { ...next.me, cosmetics: { owned: [...next.me.cosmetics.owned], equipped: { ...next.me.cosmetics.equipped, [applySlot]: applyId } } } }
      }
      if (next.cosmetics && applySlot !== "") {
        next = {
          ...next,
          cosmetics: next.cosmetics.map((x) => {
            if (x.kind !== applySlot) return x
            return { ...x, equipped: x.id === applyId }
          }),
        }
      }
      return next
    })
    // Pure optimistic apply above is instantly visible; fire-and-forget send
    // (queued offline, replayed on reconnect). Only a server nack rolls back.
    fireBuy(payload)
  }, [pushNotice, fireBuy, inflightBuyItem, syncPendingKey])

  const chooseSpec = useCallback((id: string) => {    if (!guildValid) {
      setError("Choose a bakery first")
      return
    }
    const next = id.trim().toLowerCase()
    if (next === "") return
    if (inflightBuyItem("spec", id)) return
    // Client-side lock mirror (backend stays authoritative): never fire a
    // doomed request when XP/cooldown gates are unmet — the UI disables these
    // buttons, so reaching here means a stale modal/race. Silent return keeps
    // the "no error toast on click when locked" contract; the server still
    // rejects any forged request with locked/cooldown.
    const s = snapRef.current
    if (s) {
      const lvl = s.me.level ?? 1
      if (lvl < 2) return
      const last = s.me.lastSpecLevel ?? 0
      const cur = (s.me.specialization ?? "").trim().toLowerCase()
      if (cur === next) {
        // First explicit confirmation (lastSpecLevel 0) is allowed through
        // for the backend to record; re-confirming an already-recorded spec
        // would only nack with already_chosen, so swallow it silently.
        if (last > 0) return
      } else if (last > 0 && lvl <= last) {
        return
      }
    }
    const prevSpec = snapRef.current?.me.specialization ?? "baker"
    setSnap((prev) => {
      if (!prev) return prev
      return { ...prev, me: { ...prev.me, specialization: id.toLowerCase() } }
    })
    const requestId = newRequestId()
    const payload = { type: "choose_specialization", specId: id, requestId }
    pendingBuysRef.current.set(requestId, { kind: "spec", id, itemKey: "spec:" + id.toLowerCase(), cost: 0, payload, prevCredits: snapRef.current?.me.credits ?? "0", prevCookies: snapRef.current?.bakery.cookies ?? "0", prevPower: snapRef.current?.me.clickPower ?? "1", prevUpgrades: { ...(snapRef.current?.me.upgrades ?? {}) }, prevModules: snapRef.current?.modules ? snapRef.current.modules.map((x) => ({ ...x })) as Snapshot["modules"] : null, prevProjects: snapRef.current?.projects ? snapRef.current.projects.map((x) => ({ ...x })) as Snapshot["projects"] : null, prevSpec })
    // Fire-and-forget; only a server nack rolls back.
    fireBuy({ type: "choose_specialization", specId: id, requestId })
  }, [guildValid, fireBuy, inflightBuyItem])

  // Server-authoritative claim: validate against the server snapshot, send
  // one claim_quest message (deduped by quest id server-side), and let the
  // ack/snapshot apply the reward. Never grants credits locally.
  const claimQuest = useCallback((id: string) => {
    const q = QUESTS.find((x) => x.id === id)
    if (!q) return
    const s = snapRef.current
    const srv = s?.quests?.find((x) => x.id === id)
    if (srv?.claimed) return
    const cur = srv ? srv.progress : s ? questCurrent(q, s) : 0
    const target = srv?.target ?? q.target
    if (cur < target) return
    for (const pend of pendingBuysRef.current.values()) {
      if (pend.kind === "quest" && pend.id === id) return
    }
    if (inflightBuyItem("quest", id)) return
    sfxBuy()
    const requestId = newRequestId()
    const payload = { type: "claim_quest", id, requestId }
    pendingBuysRef.current.set(requestId, { kind: "quest", id, itemKey: "quest:" + id.toLowerCase(), cost: 0, payload, prevCredits: s?.me.credits ?? "0", prevCookies: s?.bakery.cookies ?? "0", prevPower: s?.me.clickPower ?? "1", prevUpgrades: { ...(s?.me.upgrades ?? {}) }, prevModules: s?.modules ? s.modules.map((x) => ({ ...x })) as Snapshot["modules"] : null, prevProjects: s?.projects ? s.projects.map((x) => ({ ...x })) as Snapshot["projects"] : null, prevSpec: s?.me.specialization ?? "baker" })
    // Fire-and-forget; only a server nack clears. No timeout path.
    fireBuy({ type: "claim_quest", id, requestId })
  }, [fireBuy, inflightBuyItem])

  const activateAbility = useCallback((id: string) => {
    const c = connRef.current
    if (c === null) return
    const key = id.trim().toLowerCase()
    if (key === "") return
    // No stacking: one in-flight activation per ability (server cooldown
    // would reject it anyway; this keeps the button honest instantly).
    if (inflightBuyItem("ability", key)) return
    const s = snapRef.current
    const row = s?.abilities?.find((a) => a.id === key)
    const def = ABILITIES.find((a) => a.id === key)
    if (row !== undefined) {
      if (row.locked) {
        pushNotice(row.lockReason === "locked_spec" ? "Locked — needs required spec." : "Locked — level up first.", "ability")
        return
      }
      if (row.cooldownRemainingMs > 0) {
        pushNotice(`Ability recharging (${Math.ceil(row.cooldownRemainingMs / 1000)}s left).`, "ability")
        return
      }
      try {
        if (BigInt(s?.me.credits ?? "0") < BigInt(row.cost)) {
          pushNotice(`Not enough Baker Credits (need 🪙 ${fmtCompact(row.cost)}, have 🪙 ${fmtCompact(s?.me.credits ?? "0")}).`, "ability")
          return
        }
      } catch {
        return
      }
    } else if (def !== undefined && s) {
      // Pre-catalog snapshot: validate against static defs so the button
      // never fires a doomed request.
      const spec = (s.me.specialization ?? "baker").trim().toLowerCase()
      if (def.specLocked && spec !== def.specLocked.toLowerCase()) {
        pushNotice("Locked — needs required spec.", "ability")
        return
      }
      if ((s.me.level ?? 1) < def.unlockLevel) {
        pushNotice("Locked — level up first.", "ability")
        return
      }
      if (toNum(s.me.credits) < def.cost) {
        pushNotice(`Not enough Baker Credits (need 🪙 ${fmtCompact(String(def.cost))}, have 🪙 ${fmtCompact(s.me.credits)}).`, "ability")
        return
      }
    }
    const costNum = row !== undefined ? toNum(row.cost) : (def?.cost ?? 0)
    const requestId = newRequestId()
    const payload = { type: "activate_ability", id: key, requestId }
    pendingBuysRef.current.set(requestId, { kind: "ability", id: key, itemKey: "ability:" + key, cost: costNum, payload, prevCredits: s?.me.credits ?? "0", prevCookies: s?.bakery.cookies ?? "0", prevPower: s?.me.clickPower ?? "1", prevUpgrades: { ...(s?.me.upgrades ?? {}) }, prevModules: s?.modules ? s.modules.map((x) => ({ ...x })) as Snapshot["modules"] : null, prevProjects: s?.projects ? s.projects.map((x) => ({ ...x })) as Snapshot["projects"] : null, prevSpec: s?.me.specialization ?? "baker" })
    syncPendingKey("ability", key)
    // Fire-and-forget (queued offline, replayed on reconnect by requestId;
    // server dedupes). Only an explicit server nack rolls back. No timeout.
    fireBuy({ type: "activate_ability", id: key, requestId })
  }, [pushNotice, fireBuy, inflightBuyItem, syncPendingKey])

  const clickMini = useCallback((cid: string, cx: number, cy: number) => {
    const c = connRef.current
    if (!c) return
    setSnap((s) => {
      if (!s) return s
      if (!s.events.some((ev) => ev.id === "mini_cookie" && ev.cookieId === cid)) return s
      return { ...s, events: s.events.filter((ev) => !(ev.id === "mini_cookie" && ev.cookieId === cid)) }
    })
    try {
      c.send({ type: "click_mini", id: cid, x: cx, y: cy } as unknown as never)
    } catch {
      return
    }
  }, [])

  const clickSneaky = useCallback((sid: string, cx: number, cy: number) => {
    const c = connRef.current
    if (!c) return
    setSnap((s) => {
      if (!s) return s
      if (!s.events.some((ev) => ev.id === "sneaky_cookie")) return s
      return { ...s, events: s.events.filter((ev) => !(ev.id === "sneaky_cookie" && (ev.sneakyId === sid || ev.id === sid))) }
    })
    try {
      c.send({ type: "click_sneaky", id: sid, x: cx, y: cy } as unknown as never)
    } catch {
      return
    }
  }, [])

  // Single-tap golden redeem: the server validates the live event, grants
  // the instant boosted reward once per spawn, and keeps the 10s 5x frenzy
  // window live for main-cookie clicks. Fire-and-forget; the
  // golden_redeemed broadcast carries the authoritative amounts.
  const redeemGolden = useCallback((cx: number, cy: number) => {
    const c = connRef.current
    if (!c) return
    try {
      c.send({ type: "click_golden", x: cx, y: cy } as unknown as never)
    } catch {
      return
    }
  }, [])

  const retry = useCallback(() => {
    setError(null)
    clearReconnectTimer()
    reconnectAttemptsRef.current = 0
    setAttempt((a) => a + 1)
  }, [clearReconnectTimer])

  // Record raw tap for CPS board (every pointerdown, uncapped). Throttled sender below posts tester_cps.
  const recordRawTap = useCallback(() => {
    const at = Date.now()
    const arr = rawTesterTapsRef.current
    arr.push(at)
    // keep only 5s window, bounded array
    const cutoff = at - 5000
    let w = 0
    for (let i = 0; i < arr.length; i++) {
      const t = arr[i]
      if (t !== undefined && t > cutoff) arr[w++] = t
    }
    arr.length = w
    const count = arr.length
    const first = arr[0] ?? at
    const spanSec = count > 1 ? Math.max(0.001, (at - first) / 1000) : 1
    const divisor = Math.min(5, Math.max(1, spanSec))
    const cps = count === 0 ? 0 : count / divisor
    rawTesterCpsRef.current = cps
    if (cps > rawTesterPeakRef.current) rawTesterPeakRef.current = cps
    // Drop fully-expired idle buffer so array stays bounded
    if (count === 0) {
      rawTesterTapsRef.current = []
      rawTesterPeakRef.current = 0
      rawTesterCpsRef.current = 0
    }
  }, [])

  const click = useCallback((cx: number, cy: number): number => {
    const c = connRef.current
    const s = snapRef.current
    // Single live source: use snap.me.* directly when snapshot exists.
    const livePower = s ? toNum(s.me.clickPower) || 1 : Math.round(powerRef.current) || 1
    if (!c) return livePower
    const nowMs = Date.now()
    const rush = s?.events.some((ev) => ev.id === "cookie_rush" && ev.endsAt > nowMs) ?? false
    const golden = s?.events.some((ev) => ev.id === "golden_cookie" && ev.endsAt > nowMs) ?? false
    const combo = s?.me.combo ?? 0
    const preview = previewPerClick(livePower, rush, golden, combo)
    const predicted = preview.cookies
    const predictedCredits = preview.credits
    pendingRef.current.push(predicted)
    pendingCreditsRef.current.push(predictedCredits)
    setOptimistic((v) => v + predicted)
    setOptimisticCredits((v) => v + predictedCredits)
    recordRawTap()
    setSnap((prev) => {
      if (!prev) return prev
      const tc = toNum(prev.me.totalClicks ?? "0") + 1
      const contrib = (() => {
        try {
          return (BigInt(prev.me.contribution ?? "0") + BigInt(predicted)).toString()
        } catch {
          return String(toNum(prev.me.contribution ?? "0") + predicted)
        }
      })()
      return { ...prev, me: { ...prev.me, totalClicks: String(tc), contribution: contrib } }
    })
    const tid = window.setTimeout(() => {
      pendingTimersRef.current = pendingTimersRef.current.filter((x) => x !== tid)
      const idx = pendingRef.current.indexOf(predicted)
      if (idx !== -1) {
        pendingRef.current.splice(idx, 1)
        setOptimistic((v) => Math.max(0, v - predicted))
      }
      const cidx = pendingCreditsRef.current.indexOf(predictedCredits)
      if (cidx !== -1) {
        pendingCreditsRef.current.splice(cidx, 1)
        setOptimisticCredits((v) => Math.max(0, v - predictedCredits))
      }
    }, 3000)
    pendingTimersRef.current.push(tid)
    try {
      c.send({ type: "click", clickId: newRequestId(), x: cx, y: cy })
    } catch {
      return predicted
    }
    return predicted
  }, [recordRawTap])

  // Throttled tester_cps for main cookie: every tap counts (uncapped board), WS-only (new peak only).
  useEffect(() => {
    if (!ready) return
    const id = window.setInterval(() => {
      const peak = rawTesterPeakRef.current
      const cur = rawTesterCpsRef.current
      const sendVal = Math.max(peak, cur)
      if (!Number.isFinite(sendVal) || sendVal <= 0) return
      if (sendVal <= lastRawTesterSentRef.current + 0.05) return
      const nowMs2 = Date.now()
      const arr2 = rawTesterTapsRef.current
      if (arr2.length > 0) {
        const cutoff2 = nowMs2 - 5000
        const recent = arr2.filter((t) => t > cutoff2)
        if (recent.length === 0) {
          rawTesterTapsRef.current = []
          rawTesterPeakRef.current = 0
          rawTesterCpsRef.current = 0
          return
        }
      }
      const c = connRef.current
      if (c === null) return
      try {
        c.send({ type: "tester_cps", cps: sendVal } as never)
        lastRawTesterSentRef.current = sendVal
      } catch {
        return
      }
    }, 900)
    return () => window.clearInterval(id)
  }, [ready])

  const cookies = snap ? toNum(snap.bakery.cookies) : 0
  const cps = snap ? toNum(snap.bakery.cps) : 0
  const elapsed = snap ? Math.max(0, (now - snap.serverTime) / 1000) : 0
  const shownCookies = Math.floor(cookies + elapsed * cps + optimistic)
  const basePower = snap ? toNum(snap.me.clickPower) || 1 : Math.round(powerRef.current) || 1
  const perClickPreview = (() => {
    if (!snap) {
      const p = previewPerClick(basePower, false, false, 0)
      return { ...p, rush: false, golden: false, eff: effectivePower(basePower) }
    }
    const snapNow = now > 0 ? now : Date.now()
    const rush = snap.events.some((ev) => ev.id === "cookie_rush" && ev.endsAt > snapNow)
    const golden = snap.events.some((ev) => ev.id === "golden_cookie" && ev.endsAt > snapNow)
    const combo = snap.me.combo ?? 0
    const p = previewPerClick(basePower, rush, golden, combo)
    return { ...p, rush, golden, eff: effectivePower(basePower) }
  })()
  const cookiesPerClick = perClickPreview.cookies
  const creditsPerClick = perClickPreview.credits
  const clickBreakdown = (() => {
    const parts = [`base ${basePower}`, `eff ${Math.floor(perClickPreview.eff)}`, `x${CLICK_COOKIE_MULT} cookies`]
    if (perClickPreview.rush) parts.push(`rush x2`)
    if (perClickPreview.golden) parts.push(`golden x5`)
    if (perClickPreview.nextCombo > 1) parts.push(`combo x${comboMult(perClickPreview.nextCombo).toFixed(2)}`)
    return `${parts.join(` `)} = ${cookiesPerClick} cookies + ${creditsPerClick} credits`
  })()
  const automationCpsMap: Record<string, number> = { oven_assistant: 3, auto_kneader: 4, cookie_sorter: 4, delivery_assistant: 5, oven_mastery: 6, team_oven: 4, fortune_dough: 0, mech_kneader: 9, sparkle_kneader: 4, sorter_gold: 3, sorter_credit: 3, sorter_offline: 7, oven_rush: 5, oven_long: 8, oven_core: 14, clockwork_bakery: 22, star_conveyor: 36, auto_tinker: 3, auto_mechanic: 5, auto_engineer: 8, auto_factory: 12, auto_overlord: 20, auto_titan: 35, eng_scaffold: 2, eng_crane: 4, eng_foundry: 6, eng_citadel: 10, eng_metropolis: 16, eng_wonder: 28, fortune_horseshoe: 2, fortune_gem: 3, fortune_star: 5, fortune_eclipse: 8, coord_rally: 2, coord_march: 4, coord_festival: 6, coord_carnival: 10, coord_summit: 16, coord_utopia: 26 }
  const memberCps = (() => {
    if (!snap) return 0
    const u = snap.me.upgrades as Record<string, number>
    let s = 0
    for (const [k, v] of Object.entries(u)) s += (automationCpsMap[k] ?? 0) * v
    return s
  })()
  const sorterLevel = (() => {
    const u = snap?.me.upgrades as Record<string, number> | undefined
    if (!u) return 0
    return (u["cookie_sorter"] ?? 0) + (u["sorter_credit"] ?? 0) + (u["sorter_gold"] ?? 0) + (u["sorter_offline"] ?? 0)
  })()
  const creditBonus = 1 + 0.04 * Math.min(sorterLevel, 20)
  const passiveCredits = snap ? Math.floor((elapsed * memberCps * creditBonus) / 20) : 0
  const liveCredits = (snap ? toNum(snap.me.credits) : 0) + optimisticCredits + passiveCredits
  // Keep validation refs in sync with the displayed live totals.
  useEffect(() => {
    shownCookiesRef.current = shownCookies
    liveCreditsRef.current = liveCredits
  }, [shownCookies, liveCredits])

  // Server-authoritative quests: prefer snapshot progress/claimed (validated
  // + persisted server-side). Local questCurrent is fallback only.
  const quests = useMemo<StoreQuest[]>(() => {
    const server = new Map((snap?.quests ?? []).map((x) => [x.id, x]))
    return QUESTS.map((q) => {
      const s = server.get(q.id)
      const target = s?.target ?? q.target
      const reward = s?.reward ?? q.reward
      const cur = s ? s.progress : snap ? questCurrent(q, snap) : 0
      const pct = target > 0 ? Math.min(100, (cur / target) * 100) : 0
      const done = cur >= target
      return { ...q, target, reward, cur, pct, done, claimed: s ? s.claimed : false, progress: cur }
    })
  }, [snap])

  const claimableCount = useMemo(() => {
    const lvl = snap?.bakery.level ?? 1
    return quests.filter((q) => q.done && !q.claimed && (q.lockedLevel === undefined || lvl >= q.lockedLevel)).length
  }, [quests, snap])

  const presence = useMemo(() => snap?.players.filter((p) => p.active) ?? [], [snap])
  const clearOfflineBonus = useCallback(() => setOfflineBonus(null), [])

  const value = useMemo<BakeryStoreValue>(() => ({
    snapshot: snap,
    snap,
    connected,
    error,
    buyNotice,
    clearBuyNotice,
    isPending,
    pendingCount: pendingKeys.length,
    purchaseHistory,
    click,
    clickSneaky,
    clickMini,
    redeemGolden,
    buyUpgrade,
    buyModule,
    buyProject,
    buyCosmetic,
    equipCosmetic,
    chooseSpec,
    activateAbility,
    claimQuest,
    shownCookies,
    liveCredits,
    cookiesPerClick,
    creditsPerClick,
    clickBreakdown,
    basePower,
    retry,
    refresh,
    quests,
    claimableCount,
    presence,
    offlineBonus,
    clearOfflineBonus,
    sneakyFlash,
    miniFlash,
    goldenFlash,
    subscribe,
    userId,
  }), [snap, connected, error, buyNotice, clearBuyNotice, isPending, pendingKeys.length, purchaseHistory, click, clickSneaky, clickMini, redeemGolden, buyUpgrade, buyModule, buyProject, buyCosmetic, equipCosmetic, chooseSpec, activateAbility, claimQuest, shownCookies, liveCredits, cookiesPerClick, creditsPerClick, clickBreakdown, basePower, retry, refresh, quests, claimableCount, presence, offlineBonus, clearOfflineBonus, sneakyFlash, miniFlash, goldenFlash, subscribe, userId])

  return <BakeryStoreContext.Provider value={value}>{children}</BakeryStoreContext.Provider>
}
